import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaRegArrowAltCircleDown,
  FaRegArrowAltCircleUp,
  FaInfoCircle,
  FaChartPie,
  FaExchangeAlt,
  FaRegEdit,
  FaDownload,
  FaTimes,
  FaFileInvoice,
} from "react-icons/fa";
import {
  BsBoxArrowInDown,
  BsBoxArrowUpRight,
  BsArrowDownCircle,
  BsArrowUpCircle,
} from "react-icons/bs";
import { AiOutlineFundProjectionScreen } from "react-icons/ai";
import { MdPrint } from "react-icons/md";
import { IoWalletSharp } from "react-icons/io5";
import { TbZoomMoney, TbHistory } from "react-icons/tb";
import { GiWallet } from "react-icons/gi";
import { Pie, Line, Bar, Doughnut } from "react-chartjs-2";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Filler,
} from "chart.js";
import axios from "axios";
import clsx from "clsx";
import { HiOutlineCurrencyRupee } from "react-icons/hi2";
import { fetchStocknames } from '../data/Stocknames';

ChartJS.register(
  ArcElement, Tooltip, Legend, CategoryScale, LinearScale,
  PointElement, LineElement, BarElement, Title, Filler
);

// ─── TYPES ───────────────────────────────────────────────────────────────────

interface Party {
  id: number;
  name: string;
  date: string;
  number?: string;
  contact_person_name?: string;
  contact_person_number?: string;
  categories?: string | string[];
  total_amount?: number;
}
interface Stock {
  id: number | string;
  party_id: number;
  categories: any[];
  date?: string;
  total_amount: number | string;
  indirect_charges?: number | string;
}
interface Withdrawal {
  id: number;
  party_id: number;
  amount: number;
  date: string;
  catrgories?: string;
}
interface StockHistory {
  id: number;
  stock_id: string | number;
  cat_id?: string | number;
  current_price: number | string;
  current_price_date: string;
}
interface Category {
  id: string | number;
  category_name: string;
}
type TransactionType = "buy" | "sell" | "withdraw";
interface Transaction {
  id: number | string;
  party_id: number | string;
  date: string;
  type: TransactionType;
  investment_type?: string;
  stock_id?: number | string;
  lot_id?: number | string;
  category_id?: number | string;
  qty: number;
  unit?: string;
  amount: number;
  remark?: string;
  current_price?: number;
  indirect_charges?: number;
}
type FilterState = {
  from?: string;
  to?: string;
  search?: string;
  type?: string;
  stock_id?: string;
  category_id?: string;
  party_id?: string | number;
  minAmount?: string;
  maxAmount?: string;
  quick?: string;
};
type AssetHolding = {
  stock_id: string | number | undefined;
  category_id: string | number | undefined;
  party_id: number | string;
  lot_id?: number | string;
  stock?: Stock;
  investment_type: string | undefined;
  category?: any;
  total_bought: number;
  total_withdraw: number;
  total_sold: number;
  remaining_qty: number;
  unit: string;
  current_price: number;
  holding_value: number;
  history: Transaction[];
  avg_buy_price?: number;
};

// ─── INVOICE TYPES ────────────────────────────────────────────────────────────

interface InvoiceLineItem {
  id?: number;
  stock_name?: string;
  stock_id?: string;
  unit?: string | number;
  total_amount?: string | number;
  indirect_charges?: string | number;
  perUnitCharge?: string | number;
  category?: string;
  investment_type?: string;
  sell_units?: number;
  sell_amount?: number;
  date?: string;
}
interface Invoice {
  id: number;
  farm?: string;
  party: string;
  invoice_type: "purchase" | "sell";
  date: string;
  datainvoice: string | InvoiceLineItem[];
}
interface ParsedInvoice extends Invoice {
  parsedItems: InvoiceLineItem[];
  matchedItem: InvoiceLineItem;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

function formatMoney(val: number | undefined, opts?: { compact?: boolean; symbol?: boolean }) {
  if (!val || isNaN(val)) return opts?.symbol ? "₹0" : "0";
  const prefix = opts?.symbol ? "₹" : "";
  if (opts?.compact) {
    const absVal = Math.abs(val);
    if (absVal >= 1e7) return prefix + (val / 1e7).toFixed(2) + "Cr";
    if (absVal >= 1e5) return prefix + (val / 1e5).toFixed(2) + "L";
    if (absVal >= 1e3) return prefix + (val / 1e3).toFixed(1) + "k";
  }
  return prefix + Number(val).toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function safeStr(val: any): string {
  if (val === null || val === undefined) return "-";
  if (typeof val === "object") return "-";
  return String(val);
}

function downloadCSV(transactions: Transaction[], parties: Party[], format: "csv" | "excel" = "csv") {
  const sep = format === "csv" ? "," : "\t";
  const header = ["Date", "Stock", "Category", "Party", "Type", "Qty", "Unit", "Amount", "Remark"].join(sep);
  const rows = transactions.map((t) => {
    const partyObj = parties.find((p) => String(p.id) === String(t.party_id));
    return [t.date, t.stock_id, t.category_id, safeStr(partyObj?.name), t.type, t.qty, safeStr(t.unit), t.amount, safeStr(t.remark)].join(sep);
  });
  const text = header + "\n" + rows.join("\n");
  const blob = new Blob([text], { type: format === "csv" ? "text/csv" : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `transactions_${Date.now()}.${format === "csv" ? "csv" : "xls"}`;
  link.click();
  URL.revokeObjectURL(url);
}

function isRecent(dateStr: string | undefined, hours: number = 24): boolean {
  if (!dateStr) return false;
  try {
    const datePart = dateStr.length > 10 ? dateStr.slice(0, 10) : dateStr;
    const txnDate = new Date(datePart);
    const now = new Date();
    if (txnDate.getFullYear() === now.getFullYear() && txnDate.getMonth() === now.getMonth() && txnDate.getDate() === now.getDate()) return true;
    const diff = now.getTime() - txnDate.getTime();
    return diff < hours * 60 * 60 * 1000 && diff > 0;
  } catch { return false; }
}

function parseInvoiceItems(raw: string | InvoiceLineItem[]): InvoiceLineItem[] {
  if (Array.isArray(raw)) return raw;
  if (!raw || raw === "[]") return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch { return []; }
}

function matchesHoldingName(item: InvoiceLineItem, fundName: string): boolean {
  const name = (item.stock_name || item.stock_id || "").toString().trim().toLowerCase();
  return name === fundName.trim().toLowerCase();
}

// ─── INVOICE LINE TABLE ───────────────────────────────────────────────────────

function InvoiceLineTable({
  items, isSell, matchedName,
}: {
  items: InvoiceLineItem[];
  isSell: boolean;
  matchedName: string;
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-100 mt-3">
      <table className="w-full text-[11px]">
        <thead>
          <tr className={isSell ? "bg-red-50 text-red-800" : "bg-emerald-50 text-emerald-800"}>
            <th className="p-2 text-left font-bold">#</th>
            <th className="p-2 text-left font-bold">Stock / Fund</th>
            <th className="p-2 text-left font-bold">Category</th>
            <th className="p-2 text-left font-bold">Inv. Type</th>
            <th className="p-2 text-right font-bold">Units</th>
            <th className="p-2 text-right font-bold">Per Unit (₹)</th>
            <th className="p-2 text-right font-bold">Amount (₹)</th>
            {isSell && <th className="p-2 text-right font-bold">Charges</th>}
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => {
            const isMatch = matchesHoldingName(item, matchedName);
            const units = isSell ? (item.sell_units ?? 0) : parseFloat(String(item.unit ?? 0));
            const amount = isSell ? (item.sell_amount ?? 0) : parseFloat(String(item.total_amount ?? 0));
            const perUnit = isSell
              ? units > 0 ? amount / units : 0
              : parseFloat(String(item.perUnitCharge ?? 0));
            return (
              <tr
                key={i}
                className={
                  isMatch
                    ? "bg-indigo-50 ring-1 ring-inset ring-indigo-200"
                    : i % 2 === 0 ? "bg-white" : "bg-slate-50"
                }
              >
                <td className="p-2 text-slate-400 font-mono">{i + 1}</td>
                <td className="p-2">
                  <span className={isMatch ? "font-black text-indigo-700" : "font-medium text-slate-700"}>
                    {item.stock_name || item.stock_id || "—"}
                  </span>
                  {isMatch && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-full text-[9px] font-black bg-indigo-500 text-white uppercase tracking-wide">
                      this fund
                    </span>
                  )}
                </td>
                <td className="p-2 text-slate-500">{item.category || "—"}</td>
                <td className="p-2">
                  {item.investment_type && (
                    <span className={clsx(
                      "px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wide",
                      item.investment_type === "direct" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                    )}>
                      {item.investment_type}
                    </span>
                  )}
                </td>
                <td className="p-2 text-right font-semibold text-slate-700">
                  {Number(units).toLocaleString("en-IN", { maximumFractionDigits: 4 })}
                </td>
                <td className="p-2 text-right text-slate-600">
                  {formatMoney(perUnit, { symbol: true })}
                </td>
                <td className={`p-2 text-right font-bold ${isMatch ? "text-indigo-700" : isSell ? "text-red-600" : "text-emerald-700"}`}>
                  {formatMoney(amount, { symbol: true })}
                </td>
                {isSell && (
                  <td className="p-2 text-right text-orange-600">
                    {item.indirect_charges && Number(item.indirect_charges) > 0
                      ? formatMoney(Number(item.indirect_charges), { symbol: true })
                      : "—"}
                  </td>
                )}
              </tr>
            );
          })}
          {/* totals row */}
          <tr className={isSell ? "bg-red-100 font-black text-red-800" : "bg-emerald-100 font-black text-emerald-800"}>
            <td colSpan={4} className="p-2 text-right text-xs">Total</td>
            <td className="p-2 text-right text-xs">
              {items.reduce((s, it) => s + parseFloat(String(isSell ? (it.sell_units ?? 0) : (it.unit ?? 0))), 0)
                .toLocaleString("en-IN", { maximumFractionDigits: 4 })}
            </td>
            <td className="p-2"></td>
            <td className="p-2 text-right text-xs">
              {formatMoney(
                items.reduce((s, it) => s + parseFloat(String(isSell ? (it.sell_amount ?? 0) : (it.total_amount ?? 0))), 0),
                { symbol: true }
              )}
            </td>
            {isSell && <td className="p-2"></td>}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

// ─── INVOICE HISTORY TAB ─────────────────────────────────────────────────────

type InvoiceFilterType = "all" | "purchase" | "sell";

function InvoiceHistoryTab({
  matchedInvoices, allParties, fundName, isLoading,
}: {
  matchedInvoices: ParsedInvoice[];
  allParties: Party[];
  fundName: string;
  isLoading: boolean;
}) {
  const [filter, setFilter] = useState<InvoiceFilterType>("all");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  function toggleExpand(id: number) {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  const filtered = useMemo(
    () => filter === "all" ? matchedInvoices : matchedInvoices.filter((inv) => inv.invoice_type === filter),
    [matchedInvoices, filter]
  );

  // Running closing units (cumulative across all invoices, chronological)
  const closingUnitsMap = useMemo(() => {
    let running = 0;
    const map = new Map<number, number>();
    matchedInvoices.forEach((inv) => {
      const isSell = inv.invoice_type === "sell";
      const units = isSell
        ? parseFloat(String(inv.matchedItem.sell_units ?? 0))
        : parseFloat(String(inv.matchedItem.unit ?? 0));
      running = isSell ? running - units : running + units;
      map.set(inv.id, Math.max(0, running));
    });
    return map;
  }, [matchedInvoices]);

  const totalPurchasedAmt = useMemo(() =>
    matchedInvoices.filter(i => i.invoice_type === "purchase")
      .reduce((s, inv) => s + parseFloat(String(inv.matchedItem.total_amount ?? 0)), 0),
    [matchedInvoices]
  );
  const totalSoldAmt = useMemo(() =>
    matchedInvoices.filter(i => i.invoice_type === "sell")
      .reduce((s, inv) => s + parseFloat(String(inv.matchedItem.sell_amount ?? 0)), 0),
    [matchedInvoices]
  );
  const totalUnitsBought = useMemo(() =>
    matchedInvoices.filter(i => i.invoice_type === "purchase")
      .reduce((s, inv) => s + parseFloat(String(inv.matchedItem.unit ?? 0)), 0),
    [matchedInvoices]
  );
  const totalUnitsSold = useMemo(() =>
    matchedInvoices.filter(i => i.invoice_type === "sell")
      .reduce((s, inv) => s + parseFloat(String(inv.matchedItem.sell_units ?? 0)), 0),
    [matchedInvoices]
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-400 gap-3">
        <div className="w-8 h-8 border-4 border-indigo-200 border-t-indigo-500 rounded-full animate-spin" />
        <span className="text-sm font-semibold animate-pulse">Loading invoice records…</span>
      </div>
    );
  }

  if (!matchedInvoices.length) {
    return (
      <div className="flex flex-col items-center justify-center py-14 text-slate-400">
        <FaFileInvoice className="text-4xl mb-3 opacity-30" />
        <div className="font-semibold text-sm">No invoice records found for this fund</div>
        <div className="text-xs mt-1 opacity-70">Transactions may have been entered directly, not via invoices</div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          {
            label: "Invoices", icon: "📄",
            value: String(matchedInvoices.length),
            sub: `${matchedInvoices.filter(i => i.invoice_type === "purchase").length} buy · ${matchedInvoices.filter(i => i.invoice_type === "sell").length} sell`,
            color: "from-slate-50 to-slate-100", text: "text-slate-700",
          },
          {
            label: "Units Purchased", icon: "📥",
            value: totalUnitsBought.toLocaleString("en-IN", { maximumFractionDigits: 4 }),
            sub: formatMoney(totalPurchasedAmt, { symbol: true, compact: true }),
            color: "from-emerald-50 to-emerald-100", text: "text-emerald-700",
          },
          {
            label: "Units Sold", icon: "📤",
            value: totalUnitsSold.toLocaleString("en-IN", { maximumFractionDigits: 4 }),
            sub: formatMoney(totalSoldAmt, { symbol: true, compact: true }),
            color: "from-red-50 to-red-100", text: "text-red-700",
          },
          {
            label: "Net Units", icon: "⚖️",
            value: (totalUnitsBought - totalUnitsSold).toLocaleString("en-IN", { maximumFractionDigits: 4 }),
            sub: `Net invested: ${formatMoney(totalPurchasedAmt - totalSoldAmt, { symbol: true, compact: true })}`,
            color: "from-indigo-50 to-indigo-100", text: "text-indigo-700",
          },
        ].map((c) => (
          <div key={c.label} className={`bg-gradient-to-br ${c.color} rounded-xl p-3 border border-white shadow-sm`}>
            <div className="text-xl mb-1">{c.icon}</div>
            <div className={`text-base font-black ${c.text}`}>{c.value}</div>
            <div className="text-[10px] text-slate-500 font-semibold">{c.label}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{c.sub}</div>
          </div>
        ))}
      </div>

      {/* Filter pills */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-slate-500 font-semibold mr-1">Filter:</span>
        {(["all", "purchase", "sell"] as InvoiceFilterType[]).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-3 py-1 rounded-full text-xs font-bold border transition-all ${
              filter === f
                ? f === "sell" ? "bg-red-100 text-red-700 border-red-200"
                  : f === "purchase" ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                  : "bg-indigo-100 text-indigo-700 border-indigo-200"
                : "bg-white text-slate-500 border-slate-200 hover:bg-slate-50"
            }`}
          >
            {f === "all" ? `All (${matchedInvoices.length})`
              : f === "purchase" ? `Purchase (${matchedInvoices.filter(i => i.invoice_type === "purchase").length})`
              : `Sell (${matchedInvoices.filter(i => i.invoice_type === "sell").length})`}
          </button>
        ))}
        <button
          className="ml-auto text-xs text-indigo-500 font-semibold hover:underline"
          onClick={() => {
            if (expandedIds.size === filtered.length) setExpandedIds(new Set());
            else setExpandedIds(new Set(filtered.map(i => i.id)));
          }}
        >
          {expandedIds.size === filtered.length ? "Collapse all" : "Expand all"}
        </button>
      </div>

      {/* Invoice rows */}
      <div className="space-y-2">
        {filtered.map((inv) => {
          const isSell = inv.invoice_type === "sell";
          const isExpanded = expandedIds.has(inv.id);
          const party = allParties.find((p) => String(p.id) === String(inv.party));
          const matchedUnits = isSell
            ? parseFloat(String(inv.matchedItem.sell_units ?? 0))
            : parseFloat(String(inv.matchedItem.unit ?? 0));
          const matchedAmount = isSell
            ? parseFloat(String(inv.matchedItem.sell_amount ?? 0))
            : parseFloat(String(inv.matchedItem.total_amount ?? 0));
          const perUnit = isSell
            ? matchedUnits > 0 ? matchedAmount / matchedUnits : 0
            : parseFloat(String(inv.matchedItem.perUnitCharge ?? 0));
          const indirectCharges = Number(inv.matchedItem.indirect_charges ?? 0);
          const invDate = inv.date?.slice(0, 10) ?? "—";
          const closingUnits = closingUnitsMap.get(inv.id) ?? 0;

          return (
            <div key={inv.id} className="rounded-xl border border-slate-200 overflow-hidden bg-white shadow-sm">
              {/* Row header */}
              <button
                className="w-full text-left px-4 py-3 flex items-center gap-3 hover:bg-slate-50 transition-colors"
                onClick={() => toggleExpand(inv.id)}
              >
                {/* Type badge */}
                <span className={`flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-black flex-shrink-0 ${isSell ? "bg-red-100 text-red-700" : "bg-emerald-100 text-emerald-700"}`}>
                  {isSell ? <BsArrowUpCircle className="text-sm" /> : <BsArrowDownCircle className="text-sm" />}
                  {isSell ? "Sell" : "Purchase"}
                </span>

                {/* Meta */}
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-bold text-slate-700 flex items-center gap-2 flex-wrap">
                    <span className="px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 text-[10px] font-semibold">
                      {safeStr(party?.name) !== "-" ? safeStr(party?.name) : `Party ${inv.party}`}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-400 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span>📅 {invDate}</span>
                    {matchedUnits > 0 && (
                      <span className={isSell ? "text-red-500" : "text-emerald-600"}>
                        {isSell ? "−" : "+"}{matchedUnits.toLocaleString("en-IN", { maximumFractionDigits: 4 })} units
                      </span>
                    )}
                    {perUnit > 0 && <span>💰 ₹{perUnit.toLocaleString("en-IN", { maximumFractionDigits: 2 })} / unit</span>}
                    {!isSell && indirectCharges > 0 && <span className="text-orange-500">⚡ Charges: {formatMoney(indirectCharges, { symbol: true })}</span>}
                  </div>
                </div>

                {/* Amount */}
                <div className="text-right flex-shrink-0">
                  <div className={`text-[15px] font-black ${isSell ? "text-red-600" : "text-emerald-700"}`}>
                    {isSell ? "−" : "+"}{formatMoney(matchedAmount, { symbol: true, compact: true })}
                  </div>
                  <div className="text-[10px] text-slate-400">this fund only</div>
                </div>

                {/* Closing Units */}
                <div className="text-right flex-shrink-0 ml-1 border-l border-slate-100 pl-3 hidden sm:block">
                  <div className="text-[13px] font-black text-indigo-700">
                    {closingUnits.toLocaleString("en-IN", { maximumFractionDigits: 4 })}
                  </div>
                  <div className="text-[9px] text-slate-400 font-semibold uppercase tracking-wide">closing units</div>
                </div>

                {/* Chevron */}
                <span className={`text-slate-400 transition-transform duration-200 flex-shrink-0 text-base ${isExpanded ? "rotate-180" : ""}`}>▾</span>
              </button>

              {/* Expanded section */}
              {isExpanded && (
                <div className="border-t border-slate-100 bg-slate-50/60">
                  <div className="px-4 pt-4 pb-4">
                    <div className="text-[11px] font-black text-indigo-700 uppercase tracking-widest mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-full bg-indigo-500 inline-block"></span>
                      Fund Details
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
                      {[
                        { label: "Party", value: safeStr(party?.name) !== "-" ? safeStr(party?.name) : `Party ${inv.party}`, icon: "🤝" },
                        { label: "Date", value: invDate, icon: "📅" },
                        { label: isSell ? "Units Sold" : "Units Bought", value: matchedUnits.toLocaleString("en-IN", { maximumFractionDigits: 4 }), icon: "📦" },
                        { label: "Price / Unit", value: perUnit > 0 ? `₹${perUnit.toLocaleString("en-IN", { maximumFractionDigits: 2 })}` : "—", icon: "💰" },
                        { label: "Total Amount", value: formatMoney(matchedAmount, { symbol: true }), icon: "💵" },
                        { label: "Indirect Charges", value: indirectCharges > 0 ? formatMoney(indirectCharges, { symbol: true }) : "₹0", icon: "⚡" },
                        { label: "Category", value: inv.matchedItem.category || "—", icon: "🏷️" },
                        { label: "Inv. Type", value: inv.matchedItem.investment_type || "—", icon: "📋" },
                        // ← NEW: closing units in the detail card
                        { label: "Closing Units", value: closingUnits.toLocaleString("en-IN", { maximumFractionDigits: 4 }), icon: "⚖️" },
                      ].map((d) => (
                        <div
                          key={d.label}
                          className={`bg-white rounded-lg px-3 py-2 border ${d.label === "Closing Units" ? "border-indigo-300 bg-indigo-50" : "border-indigo-100"}`}
                        >
                          <div className="text-[10px] text-slate-400 font-semibold">{d.icon} {d.label}</div>
                          <div className={`text-[12px] font-bold mt-0.5 ${d.label === "Closing Units" ? "text-indigo-700" : "text-slate-700"}`}>
                            {d.value}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
// ─── FUND DETAIL PANEL ────────────────────────────────────────────────────────

type TabKey = "overview" | "invoices" | "pnl";

interface FundDetailPanelProps {
  holding: AssetHolding;
  allParties: Party[];
  stockHistory: StockHistory[];
  lotNumber?: number;
  onClose: () => void;
}

function FundDetailPanel({ holding, allParties, stockHistory, lotNumber, onClose }: FundDetailPanelProps) {
  const [activeTab, setActiveTab] = useState<TabKey>("overview");
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoiceError, setInvoiceError] = useState<string | null>(null);

  // Derived names
  const fundName = (() => {
    const cat = holding.category;
    if (cat?.stock_name && typeof cat.stock_name !== "object") return String(cat.stock_name);
    if (cat?.category_name && typeof cat.category_name !== "object") return String(cat.category_name);
    if (cat?.category && typeof cat.category !== "object") return String(cat.category);
    return safeStr(holding.stock_id);
  })();

  const categoryName = (() => {
    const cat = holding.category;
    if (cat?.category_name && typeof cat.category_name !== "object") return String(cat.category_name);
    if (cat?.category && typeof cat.category !== "object") return String(cat.category);
    return safeStr(holding.category_id);
  })();

  // Fetch invoices on mount
  useEffect(() => {
    setInvoicesLoading(true);
    setInvoiceError(null);
    axios
      .get("https://stockaadvikbackend.aadviktech.com/auth/invoice")
      .then((res) => setAllInvoices(Array.isArray(res.data) ? res.data : []))
      .catch((err) => setInvoiceError(err?.response?.data?.message || err?.message || "Failed to load invoices."))
      .finally(() => setInvoicesLoading(false));
  }, []);

  // Match invoices to this holding's fund name
  const matchedInvoices: ParsedInvoice[] = useMemo(() => {
    return allInvoices
      .map((inv): ParsedInvoice | null => {
        const parsedItems = parseInvoiceItems(inv.datainvoice);
        const matchedItem = parsedItems.find((item) => matchesHoldingName(item, fundName));
        if (!matchedItem) return null;
        return { ...inv, parsedItems, matchedItem };
      })
      .filter((x): x is ParsedInvoice => x !== null)
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [allInvoices, fundName]);

  // Transaction breakdowns
  const buys      = holding.history.filter(t => t.type === "buy");
  const sells     = holding.history.filter(t => t.type === "sell");
  const withdraws = holding.history.filter(t => t.type === "withdraw");

  const totalInvested  = buys.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const totalSold      = sells.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const totalWithdrawn = withdraws.reduce((s, t) => s + (Number(t.amount) || 0), 0);
  const pnl            = (holding.holding_value + totalSold + totalWithdrawn) - totalInvested;
  const pnlPct         = totalInvested ? ((pnl / totalInvested) * 100).toFixed(2) : "0.00";

  // Party map
  const partyMap = new Map<string, { name: string; invested: number; sold: number; withdrawn: number }>();
  holding.history.forEach(t => {
    const party = allParties.find(p => String(p.id) === String(t.party_id));
    const pName = safeStr(party?.name) !== "-" ? safeStr(party?.name) : `Party ${t.party_id}`;
    if (!partyMap.has(pName)) partyMap.set(pName, { name: pName, invested: 0, sold: 0, withdrawn: 0 });
    const rec = partyMap.get(pName)!;
    if (t.type === "buy")      rec.invested   += Number(t.amount) || 0;
    if (t.type === "sell")     rec.sold        += Number(t.amount) || 0;
    if (t.type === "withdraw") rec.withdrawn   += Number(t.amount) || 0;
  });

  // Price history & qty timeline
  const priceHistory = stockHistory
    .filter(h => String(h.stock_id) === String(holding.stock_id))
    .sort((a, b) => new Date(a.current_price_date).getTime() - new Date(b.current_price_date).getTime());

  const sortedHistory = [...holding.history].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
  let runningQty = 0;
  const qtyTimeline = sortedHistory.map(t => {
    if (t.type === "buy")      runningQty += t.qty;
    if (t.type === "sell")     runningQty -= t.qty;
    if (t.type === "withdraw") runningQty -= t.qty;
    return { date: t.date, qty: Math.max(0, runningQty) };
  });

  const invoiceBadge = invoicesLoading ? "…" : matchedInvoices.length > 0 ? String(matchedInvoices.length) : null;

  const TABS: { key: TabKey; label: string; badge?: string | null }[] = [
    { key: "overview", label: "Overview" },
    { key: "invoices", label: "Fund History", badge: invoiceBadge },
    { key: "pnl",      label: "P&L Detail" },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto flex flex-col" style={{ fontFamily: "'DM Sans', 'Segoe UI', sans-serif" }}>

        {/* Header */}
        <div className="sticky top-0 z-10 bg-gradient-to-r from-slate-800 to-slate-700 text-white px-8 py-5 rounded-t-2xl flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3 mb-1">
              <div className="text-xs font-semibold tracking-widest uppercase text-slate-400">Fund Detail</div>
              {lotNumber != null && (
                <span className="px-2.5 py-0.5 rounded-full bg-indigo-500 text-white text-[11px] font-black tracking-wide">Lot {lotNumber}</span>
              )}
            </div>
            <div className="text-2xl font-black tracking-tight">{fundName}</div>
            <div className="text-sm text-slate-300 mt-0.5">{categoryName} &bull; {holding.investment_type || "—"}</div>
          </div>
          <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition text-white text-xl" aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* Tabs */}
        <div className="sticky top-[88px] z-10 bg-white border-b border-slate-100 px-8 flex gap-0">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-5 py-3.5 text-sm font-bold transition-all border-b-2 flex items-center gap-2 ${
                activeTab === tab.key
                  ? "border-indigo-500 text-indigo-700"
                  : "border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-200"
              }`}
            >
              {tab.label}
              {tab.badge && (
                <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-black min-w-[20px] text-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6 space-y-8">

          {/* ══════ OVERVIEW TAB ══════ */}
          {activeTab === "overview" && (
            <>
              {/* KPI Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Total Invested",  value: totalInvested,         color: "from-blue-50 to-blue-100",       text: "text-blue-700",    icon: "💰" },
                  { label: "Current Value",   value: holding.holding_value, color: "from-emerald-50 to-emerald-100", text: "text-emerald-700", icon: "📈" },
                  { label: "Total Sold",      value: totalSold,             color: "from-red-50 to-red-100",         text: "text-red-700",     icon: "🔴" },
                  { label: "Total Withdrawn", value: totalWithdrawn,        color: "from-amber-50 to-amber-100",     text: "text-amber-700",   icon: "🏧" },
                ].map(c => (
                  <div key={c.label} className={`bg-gradient-to-br ${c.color} rounded-xl p-4 border border-white shadow-sm`}>
                    <div className="text-2xl mb-1">{c.icon}</div>
                    <div className={`text-xl font-black ${c.text}`}>{formatMoney(c.value, { symbol: true, compact: true })}</div>
                    <div className="text-xs text-slate-500 font-semibold mt-1">{c.label}</div>
                  </div>
                ))}
              </div>

              {/* P&L Banner */}
              <div className={`rounded-xl px-6 py-4 flex items-center gap-4 ${pnl >= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                <span className="text-3xl">{pnl >= 0 ? "🟢" : "🔴"}</span>
                <div>
                  <div className="text-xs font-bold tracking-widest uppercase text-slate-500">Profit / Loss</div>
                  <div className={`text-2xl font-black ${pnl >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {pnl >= 0 ? "+" : ""}{formatMoney(Math.abs(pnl), { symbol: true })}
                    <span className="text-base font-semibold ml-3 opacity-80">({pnl >= 0 ? "+" : ""}{pnlPct}%)</span>
                  </div>
                </div>
                <div className="ml-auto text-right">
                  <div className="text-xs text-slate-500 font-semibold">Remaining Units</div>
                  <div className="text-xl font-black text-slate-700">{holding.remaining_qty.toFixed(4)}</div>
                  <div className="text-xs text-slate-400">@ {formatMoney(holding.current_price, { symbol: true })} / unit</div>
                </div>
              </div>

              {/* Charts */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100">
                  <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase flex items-center gap-2"><span>📊</span> Units Over Time</div>
                  <Line
                    data={{
                      labels: qtyTimeline.map(p => p.date),
                      datasets: [{ label: "Remaining Units", data: qtyTimeline.map(p => p.qty), borderColor: "#6366f1", backgroundColor: "rgba(99,102,241,0.08)", fill: true, tension: 0.4, pointRadius: 4, pointBackgroundColor: "#6366f1" }],
                    }}
                    options={{ responsive: true, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: "#64748b", font: { size: 10 } }, grid: { color: "#f1f5f9" } }, y: { ticks: { color: "#64748b", font: { size: 10 } }, grid: { color: "#f1f5f9" }, beginAtZero: true } } }}
                    height={160}
                  />
                </div>

                <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 flex flex-col items-center">
                  <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase w-full flex items-center gap-2"><span>🥧</span> Capital Breakdown</div>
                  <Doughnut
                    data={{
                      labels: ["Current Value", "Sold", "Withdrawn"],
                      datasets: [{ data: [holding.holding_value, totalSold, totalWithdrawn], backgroundColor: ["#6366f1", "#ef4444", "#f59e0b"], borderWidth: 3, borderColor: "#fff" }],
                    }}
                    options={{ plugins: { legend: { position: "bottom", labels: { color: "#475569", font: { size: 11, weight: "bold" }, padding: 16, usePointStyle: true } } }, cutout: "65%" }}
                    height={180}
                  />
                </div>

                {priceHistory.length > 1 && (
                  <div className="bg-slate-50 rounded-xl p-5 border border-slate-100 md:col-span-2">
                    <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase flex items-center gap-2"><span>💹</span> Price History</div>
                    <Line
                      data={{
                        labels: priceHistory.map(h => h.current_price_date.slice(0, 10)),
                        datasets: [{ label: "Price (₹)", data: priceHistory.map(h => Number(h.current_price) || 0), borderColor: "#10b981", backgroundColor: "rgba(16,185,129,0.07)", fill: true, tension: 0.4, pointRadius: 3, pointBackgroundColor: "#10b981" }],
                      }}
                      options={{ responsive: true, plugins: { legend: { display: false } }, scales: { x: { ticks: { color: "#64748b", font: { size: 10 } }, grid: { color: "#f1f5f9" } }, y: { ticks: { color: "#64748b", font: { size: 10 }, callback: (v: any) => "₹" + Number(v).toLocaleString("en-IN") }, grid: { color: "#f1f5f9" }, beginAtZero: false } } }}
                      height={130}
                    />
                  </div>
                )}
              </div>

              {/* Party-wise Breakdown */}
              {partyMap.size > 0 && (
                <div>
                  <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase flex items-center gap-2"><span>🤝</span> Party-wise Breakdown</div>
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-slate-100 text-slate-600 font-bold">
                          <th className="p-3 text-left">Party</th>
                          <th className="p-3 text-right">Invested</th>
                          <th className="p-3 text-right">Sold</th>
                          <th className="p-3 text-right">Withdrawn</th>
                          <th className="p-3 text-right">Net Position</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Array.from(partyMap.values()).map((p, i) => {
                          const net = p.invested - p.sold - p.withdrawn;
                          return (
                            <tr key={p.name} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                              <td className="p-3 font-semibold text-slate-700">{p.name}</td>
                              <td className="p-3 text-right text-blue-600 font-bold">{formatMoney(p.invested, { symbol: true })}</td>
                              <td className="p-3 text-right text-red-500 font-bold">{formatMoney(p.sold, { symbol: true })}</td>
                              <td className="p-3 text-right text-amber-600 font-bold">{formatMoney(p.withdrawn, { symbol: true })}</td>
                              <td className={`p-3 text-right font-black ${net >= 0 ? "text-emerald-600" : "text-red-500"}`}>
                                {net >= 0 ? "+" : ""}{formatMoney(Math.abs(net), { symbol: true })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </>
          )}

          {/* ══════ INVOICE HISTORY TAB ══════ */}
          {activeTab === "invoices" && (
            <>
              {invoiceError ? (
                <div className="rounded-xl bg-red-50 border border-red-200 text-red-700 px-5 py-4 text-sm font-semibold">
                  ⚠️ {invoiceError}
                </div>
              ) : (
                <InvoiceHistoryTab
                  matchedInvoices={matchedInvoices}
                  allParties={allParties}
                  fundName={fundName}
                  isLoading={invoicesLoading}
                />
              )}
            </>
          )}

          {/* ══════ P&L DETAIL TAB ══════ */}
          {activeTab === "pnl" && (
            <>
              {/* Purchase History */}
              {buys.length > 0 && (
                <div>
                  <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase flex items-center gap-2"><span>🛒</span> Purchase History</div>
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-emerald-50 text-emerald-800 font-bold">
                          <th className="p-3 text-left">#</th>
                          <th className="p-3 text-left">Date</th>
                          <th className="p-3 text-right">Qty</th>
                          <th className="p-3 text-right">Amount</th>
                          <th className="p-3 text-right">Price/Unit</th>
                          <th className="p-3 text-left">Party</th>
                          <th className="p-3 text-left">Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {buys.map((t, i) => {
                          const party = allParties.find(p => String(p.id) === String(t.party_id));
                          const pricePerUnit = t.qty > 0 ? (Number(t.amount) / t.qty) : 0;
                          return (
                            <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-emerald-50/40"}>
                              <td className="p-3 text-slate-400 font-mono">{i + 1}</td>
                              <td className="p-3 font-semibold text-slate-700">{t.date}</td>
                              <td className="p-3 text-right font-bold text-emerald-700">{t.qty}</td>
                              <td className="p-3 text-right font-bold text-blue-700">{formatMoney(Number(t.amount), { symbol: true })}</td>
                              <td className="p-3 text-right text-slate-600">{formatMoney(pricePerUnit, { symbol: true })}</td>
                              <td className="p-3 text-slate-600">{safeStr(party?.name)}</td>
                              <td className="p-3 text-slate-400">{safeStr(t.remark)}</td>
                            </tr>
                          );
                        })}
                        <tr className="bg-emerald-100 font-black text-emerald-800">
                          <td colSpan={2} className="p-3">Total</td>
                          <td className="p-3 text-right">{buys.reduce((s, t) => s + t.qty, 0).toFixed(4)}</td>
                          <td className="p-3 text-right">{formatMoney(totalInvested, { symbol: true })}</td>
                          <td className="p-3 text-right">{formatMoney(holding.avg_buy_price, { symbol: true })}<span className="text-[10px] font-normal ml-1 opacity-70">avg</span></td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Sell History */}
              {sells.length > 0 && (
                <div>
                  <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase flex items-center gap-2"><span>💸</span> Sell History</div>
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-red-50 text-red-800 font-bold">
                          <th className="p-3 text-left">#</th>
                          <th className="p-3 text-left">Date</th>
                          <th className="p-3 text-right">Qty Sold</th>
                          <th className="p-3 text-right">Amount</th>
                          <th className="p-3 text-right">Price/Unit</th>
                          <th className="p-3 text-right">Indirect Charges</th>
                          <th className="p-3 text-left">Party</th>
                        </tr>
                      </thead>
                      <tbody>
                        {sells.map((t, i) => {
                          const party = allParties.find(p => String(p.id) === String(t.party_id));
                          const pricePerUnit = t.qty > 0 ? (Number(t.amount) / t.qty) : 0;
                          return (
                            <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-red-50/40"}>
                              <td className="p-3 text-slate-400 font-mono">{i + 1}</td>
                              <td className="p-3 font-semibold text-slate-700">{t.date}</td>
                              <td className="p-3 text-right font-bold text-red-600">{t.qty}</td>
                              <td className="p-3 text-right font-bold text-blue-700">{formatMoney(Number(t.amount), { symbol: true })}</td>
                              <td className="p-3 text-right text-slate-600">{formatMoney(pricePerUnit, { symbol: true })}</td>
                              <td className="p-3 text-right text-orange-600">{t.indirect_charges ? formatMoney(t.indirect_charges, { symbol: true }) : "—"}</td>
                              <td className="p-3 text-slate-600">{safeStr(party?.name)}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Withdrawal History */}
              {withdraws.length > 0 && (
                <div>
                  <div className="font-bold text-slate-700 mb-3 text-sm tracking-wide uppercase flex items-center gap-2"><span>🏧</span> Withdrawal History</div>
                  <div className="overflow-x-auto rounded-xl border border-slate-100">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-amber-50 text-amber-800 font-bold">
                          <th className="p-3 text-left">#</th>
                          <th className="p-3 text-left">Date</th>
                          <th className="p-3 text-right">Amount</th>
                          <th className="p-3 text-left">Party</th>
                          <th className="p-3 text-left">Remark</th>
                        </tr>
                      </thead>
                      <tbody>
                        {withdraws.map((t, i) => {
                          const party = allParties.find(p => String(p.id) === String(t.party_id));
                          return (
                            <tr key={t.id} className={i % 2 === 0 ? "bg-white" : "bg-amber-50/40"}>
                              <td className="p-3 text-slate-400 font-mono">{i + 1}</td>
                              <td className="p-3 font-semibold text-slate-700">{t.date}</td>
                              <td className="p-3 text-right font-bold text-amber-700">{formatMoney(Number(t.amount), { symbol: true })}</td>
                              <td className="p-3 text-slate-600">{safeStr(party?.name)}</td>
                              <td className="p-3 text-slate-400">{safeStr(t.remark)}</td>
                            </tr>
                          );
                        })}
                        <tr className="bg-amber-100 font-black text-amber-800">
                          <td colSpan={2} className="p-3">Total</td>
                          <td className="p-3 text-right">{formatMoney(withdraws.reduce((s, t) => s + Number(t.amount), 0), { symbol: true })}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* P&L Summary */}
              <div className={`rounded-xl px-6 py-5 flex flex-col gap-3 ${pnl >= 0 ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`}>
                <div className="font-bold text-slate-700 text-sm tracking-wide uppercase">P&L Summary</div>
                {[
                  { label: "Total Invested",  value: totalInvested,         color: "text-blue-700" },
                  { label: "Current Value",   value: holding.holding_value, color: "text-emerald-700" },
                  { label: "Total Sold",      value: totalSold,             color: "text-red-600" },
                  { label: "Total Withdrawn", value: totalWithdrawn,        color: "text-amber-600" },
                ].map(row => (
                  <div key={row.label} className="flex justify-between items-center text-sm">
                    <span className="text-slate-500 font-semibold">{row.label}</span>
                    <span className={`font-black ${row.color}`}>{formatMoney(row.value, { symbol: true })}</span>
                  </div>
                ))}
                <div className="border-t border-slate-200 pt-3 flex justify-between items-center">
                  <span className="font-bold text-slate-700 text-sm">Net P&L</span>
                  <span className={`text-xl font-black ${pnl >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                    {pnl >= 0 ? "+" : ""}{formatMoney(Math.abs(pnl), { symbol: true })}
                    <span className="text-sm font-semibold ml-2 opacity-80">({pnl >= 0 ? "+" : ""}{pnlPct}%)</span>
                  </span>
                </div>
              </div>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

// ─── TRANSACTION DETAIL MODAL ────────────────────────────────────────────────

function TransactionDetailModal({ transaction, onClose, stock, category, party }: {
  transaction: Transaction; onClose: () => void; stock: Stock | undefined; category: Category | any; party: Party | undefined;
}) {
  if (!transaction) return null;
  return (
    <div className="fixed z-50 inset-0 bg-[#1119]/50 flex items-center justify-center">
      <div className="bg-white shadow-2xl text-[#374151] p-6 rounded-lg min-w-[340px] max-w-[96vw] relative animate-fade-in">
        <button className="absolute top-2 right-2 text-xl text-[#868cab] hover:text-[#222] p-0.5" onClick={onClose}>×</button>
        <div className="flex items-center gap-2 mb-4">
          <FaInfoCircle className="text-[#38b6ff] text-xl" />
          <span className="font-semibold text-lg tracking-wide">
            Transaction Detail
            <span className="ml-2 inline-flex items-center gap-1 px-2 py-1 rounded bg-[#f6f8fc] text-[#8ca8e2] text-xs font-bold">#{transaction.id}</span>
          </span>
        </div>
        <dl className="flex flex-wrap gap-2 mb-2 text-[13px]">
          {[
            ["Type", transaction.type], ["Date", transaction.date], ["Stock", safeStr(transaction.stock_id)],
            ["Category", safeStr(transaction.category_id)], ["Party", safeStr(party?.name)],
            ["Quantity", transaction.qty], ["Unit", safeStr(transaction.unit)],
            ["Amount", formatMoney(transaction.amount, { symbol: true })], ["Remark", safeStr(transaction.remark)],
          ].map(([label, val]) => (
            <React.Fragment key={String(label)}>
              <dt className="w-28 text-[#555] font-semibold">{label}</dt>
              <dd>{String(val)}</dd>
            </React.Fragment>
          ))}
        </dl>
        <div className="flex gap-2 mt-5">
          <button className="bg-[#eef2fd] border rounded px-3 py-1.5 text-xs font-semibold text-[#4e6189] hover:bg-[#e6f4ff]" onClick={() => window.print()}>
            <MdPrint className="inline mr-1 -mt-0.5" /> Print
          </button>
          <button className="bg-[#f7f7fb] border rounded px-3 py-1.5 text-xs font-semibold text-[#3e987a] hover:bg-[#e0fcf5]"
            onClick={() => navigator.clipboard.writeText(JSON.stringify(transaction, null, 2)).then(() => alert("Copied!"))}>
            <FaRegEdit className="inline mr-1 -mt-0.5" /> Copy JSON
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TRANSACTION EDIT MODAL ───────────────────────────────────────────────────

function TransactionEditModal({ transaction, onClose, onSave }: {
  transaction: Transaction; onClose: () => void; onSave: (t: Transaction) => Promise<void>;
}) {
  const [formdata, setFormData] = useState<any>({ ...transaction });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [allParties, setAllParties] = useState<Party[]>([]);
  const [allCategories, setAllCategories] = useState<Category[]>([]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/auth/addparty`).then(r => setAllParties(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    axios.get(`${process.env.REACT_APP_API_URL}/auth/categories`).then(r => setAllCategories(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  function handleChange(field: keyof Transaction, value: any) {
    setFormData((prev: any) => ({ ...prev, [field]: ["qty", "amount", "indirect_charges"].includes(field) ? Number(value) : value }));
  }

  return (
    <div className="fixed z-50 inset-0 bg-[#1119]/60 flex items-center justify-center">
      <div className="bg-gradient-to-br from-[#f3edfa] via-[#e9f3fd] to-[#f1fbf1] shadow-2xl text-[#222d38] rounded-2xl min-w-[420px] max-w-[98vw] w-[600px] relative border-2 border-[#e4e3f5]">
        <div className="bg-gradient-to-r from-[#faecd7] to-[#ddecfa] rounded-t-2xl flex items-center justify-between px-8 pt-6 pb-4 border-b-2 border-[#efe6c8]">
          <div className="flex items-center gap-3">
            <FaRegEdit className="text-[#f3b409] text-3xl" />
            <span className="font-black text-2xl text-[#99790a]">Edit Transaction <span className="text-base ml-2 font-bold text-[#a58527]">#{transaction.id}</span></span>
          </div>
          <button className="text-3xl text-[#868cab] hover:text-[#222] px-4 py-1.5 rounded-full" onClick={onClose} type="button">×</button>
        </div>
        <form className="grid grid-cols-2 gap-x-8 gap-y-6 px-12 py-10" onSubmit={async (e) => {
          e.preventDefault(); setIsSaving(true); setError(null);
          try { await onSave(formdata as Transaction); setIsSaving(false); }
          catch (err: any) { setIsSaving(false); setError(err?.response?.data?.error || err?.message || "Failed to save."); }
        }}>
          {[
            { label: "Date", field: "date", type: "date" },
            { label: "Qty", field: "qty", type: "number" },
            { label: "Amount (₹)", field: "amount", type: "number" },
            { label: "Unit", field: "unit", type: "text" },
          ].map(({ label, field, type }) => (
            <div key={field}>
              <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">{label}</label>
              <input type={type} className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd]"
                value={(formdata as any)[field] ?? ""} onChange={e => handleChange(field as keyof Transaction, e.target.value)} disabled={isSaving} />
            </div>
          ))}
          <div>
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">Party</label>
            <select className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full focus:ring-2 focus:ring-[#bbdbfd]"
              value={formdata.party_id ?? ""} onChange={e => handleChange("party_id", e.target.value)} disabled={isSaving}>
              <option value="">-- Select --</option>
              {allParties.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          {formdata.type === "sell" && (
            <div>
              <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">Indirect Charges</label>
              <input type="number" className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full"
                value={formdata.indirect_charges ?? ""} onChange={e => handleChange("indirect_charges", e.target.value)} disabled={isSaving} />
            </div>
          )}
          <div className="col-span-2">
            <label className="block text-[1.07rem] text-[#263349] font-semibold mb-2">Remark</label>
            <input className="border-2 border-[#dde2eb] rounded-lg px-3 py-2 text-base w-full"
              value={formdata.remark ?? ""} onChange={e => handleChange("remark", e.target.value)} disabled={isSaving} maxLength={200} />
          </div>
          {error && <div className="col-span-2 text-red-600 text-base font-bold">{error}</div>}
          <div className="col-span-2 flex gap-5 mt-4 justify-end">
            <button type="button" className="px-6 py-2 rounded-lg bg-white font-bold text-[#707eb3] border-2 border-[#c4d5fd]" onClick={onClose} disabled={isSaving}>Cancel</button>
            <button type="submit" className="px-6 py-2 rounded-lg bg-gradient-to-tr from-[#fae29e] to-[#f5c766] font-bold text-[#6c4f0b] border-2 border-[#fdeec2]" disabled={isSaving}>
              {isSaving ? "Saving..." : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── QUICK FILTERS ────────────────────────────────────────────────────────────

const QUICK_FILTERS: { key: string; label: string }[] = [
  { key: "last7", label: "Last 7 Days" },
  { key: "thisMonth", label: "This Month" },
  { key: "lastMonth", label: "Last Month" },
  { key: "ytd", label: "YTD" },
  { key: "withdraw", label: "Withdrawals" },
  { key: "highValue", label: "High Value (>₹1L)" },
];

function applyQuickFilter(key: string): Partial<FilterState> {
  const today = new Date();
  if (key === "last7") {
    return { to: new Date().toISOString().slice(0, 10), from: new Date(Date.now() - 6 * 86400000).toISOString().slice(0, 10) };
  }
  if (key === "thisMonth") return { from: new Date(today.getFullYear(), today.getMonth(), 1).toISOString().slice(0, 10), to: new Date(today.getFullYear(), today.getMonth() + 1, 0).toISOString().slice(0, 10) };
  if (key === "lastMonth") return { from: new Date(today.getFullYear(), today.getMonth() - 1, 1).toISOString().slice(0, 10), to: new Date(today.getFullYear(), today.getMonth(), 0).toISOString().slice(0, 10) };
  if (key === "ytd") return { from: new Date(today.getFullYear(), 0, 1).toISOString().slice(0, 10) };
  if (key === "withdraw") return { type: "withdraw" };
  if (key === "highValue") return { minAmount: String(1e5) };
  return {};
}

// ─── ADVANCED FILTERS ────────────────────────────────────────────────────────

function AdvancedFilters({ filters, setFilters, allParties, allStocks, allCategories }: {
  filters: FilterState; setFilters: React.Dispatch<React.SetStateAction<FilterState>>;
  allParties: Party[]; allStocks: Stock[]; allCategories: Category[];
}) {
  const location = useLocation();
  const navigate = useNavigate();

  function updateQueryParamParty(partyId: string) {
    const searchParams = new URLSearchParams(location.search);
    if (partyId) searchParams.set("party", partyId); else searchParams.delete("party");
    navigate({ pathname: location.pathname, search: searchParams.toString() }, { replace: true });
  }

  const handleChange = (field: keyof FilterState, value: any) => {
    if (field === "party_id") {
      setFilters(prev => ({ ...prev, [field]: value === "" ? undefined : value }));
      updateQueryParamParty(value);
    } else {
      setFilters(prev => ({ ...prev, [field]: value === "" || value === "All" ? undefined : value }));
    }
  };

  return (
    <div className="bg-[#e5f2fb] border border-[#d5e8fc] rounded-md p-4 flex flex-wrap gap-4 items-end mb-4">
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Party</label>
        <select className="px-2 py-1.5 rounded border text-sm min-w-[130px] focus:ring" value={filters.party_id ?? ""} onChange={e => handleChange("party_id", e.target.value)}>
          <option value="">All Parties</option>
          {allParties.map(p => <option key={p.id} value={String(p.id)}>{safeStr(p.name)}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Type</label>
        <select className="px-2 py-1.5 rounded border text-sm min-w-[110px] focus:ring" value={filters.type ?? ""} onChange={e => handleChange("type", e.target.value)}>
          <option value="">All Types</option>
          <option value="buy">Buy</option>
          <option value="withdraw">Withdraw</option>
          <option value="sell">Sell</option>
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Category</label>
        <select className="px-2 py-1.5 rounded border text-sm min-w-[130px] focus:ring" value={filters.category_id ?? ""} onChange={e => handleChange("category_id", e.target.value)}>
          <option value="">All Categories</option>
          {allCategories.map(c => <option key={c.id} value={safeStr(c.category_name)}>{safeStr(c.category_name)}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">From Date</label>
        <input type="date" className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring" value={filters.from ?? ""} onChange={e => handleChange("from", e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">To Date</label>
        <input type="date" className="px-2 py-1.5 rounded border text-sm min-w-[120px] focus:ring" value={filters.to ?? ""} onChange={e => handleChange("to", e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Search</label>
        <input type="text" placeholder="ID / remark / amount" className="px-2 py-1.5 rounded border text-sm min-w-[150px] focus:ring" value={filters.search ?? ""} onChange={e => handleChange("search", e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Min Amt</label>
        <input type="number" className="px-2 py-1.5 rounded border text-sm min-w-[90px] focus:ring" value={filters.minAmount ?? ""} onChange={e => handleChange("minAmount", e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-[#2b415a] mb-1">Max Amt</label>
        <input type="number" className="px-2 py-1.5 rounded border text-sm min-w-[90px] focus:ring" value={filters.maxAmount ?? ""} onChange={e => handleChange("maxAmount", e.target.value)} />
      </div>
      <div>
        <button className="mt-4 px-3 py-1.5 rounded bg-[#f2e3fe] text-[#6c46b7] font-bold text-xs border border-[#e2d6fb] shadow hover:bg-[#e9fafe]"
          onClick={() => { setFilters({}); updateQueryParamParty(""); }} type="button">
          Reset Filters
        </button>
      </div>
    </div>
  );
}

// ─── TYPE HELPERS ─────────────────────────────────────────────────────────────

function getTxnTypeInfo(type: TransactionType) {
  if (type === "buy") return { color: "bg-[#e6fbe9] text-[#23be5a]", icon: <BsArrowDownCircle className="text-[#47bc6b] text-2xl" />, label: "Buy / Invest" };
  if (type === "sell") return { color: "bg-[#ffe3ea] text-[#e23a4e]", icon: <BsArrowUpCircle className="text-[#ef5398] text-2xl" />, label: "Sell" };
  return { color: "bg-[#fefee8] text-[#c6a01d]", icon: <HiOutlineCurrencyRupee className="text-[#d6b01c] text-2xl" />, label: "Withdraw" };
}

// ─── SIDE PANEL ───────────────────────────────────────────────────────────────

function PhonePeSidePanel({ transactions, allStocks, allParties, categories, onSelect, selectedTxnId }: {
  transactions: Transaction[]; allStocks: Stock[]; allParties: Party[]; categories: Category[];
  onSelect: (txn: { transaction: Transaction; stock: Stock | undefined; category: Category | any; party: Party | undefined }) => void;
  selectedTxnId: string | number | null;
}) {
  React.useEffect(() => {
    if (!selectedTxnId) return;
    const el = document.getElementById("txn-item-" + selectedTxnId);
    if (el) el.scrollIntoView({ block: "center", behavior: "smooth" });
  }, [selectedTxnId]);

  function findDetails(t: Transaction) {
    const stock = allStocks.find(s => String(s.id) === String(t.stock_id));
    let category: any = null;
    if (stock && Array.isArray(stock.categories)) {
      category = stock.categories.find((cat: any) => {
        const catId = typeof cat.id === "object" ? undefined : cat.id;
        const catName = typeof cat.category_name === "object" ? undefined : cat.category_name;
        return String(catId ?? catName) === String(t.category_id);
      });
    }
    if (!category && t.category_id) category = categories.find(c => String(c.id) === String(t.category_id) || c.category_name === t.category_id);
    const party = allParties.find(p => String(p.id) === String(t.party_id));
    return { stock, category, party };
  }

  return (
    <aside className="fixed top-0 right-0 h-full bg-white border-l z-40 shadow-2xl flex flex-col px-0 pt-3 pb-3 w-[350px] max-w-full transition-all duration-300 overflow-y-auto"
      style={{ boxShadow: "-2px 0 18px -3px #b0cbf533", background: "linear-gradient(180deg,#e8f0fe 3%,#fcfcff 90%)" }}>
      <div className="text-lg px-6 pb-1.5 font-black text-[#25669a] flex gap-2 items-center sticky top-0 bg-white z-10">
        <FaExchangeAlt className="text-[#357fb2]" /> Transactions ({transactions.length})
      </div>
      <div className="flex-1 overflow-y-auto pr-3 pt-3 pb-3">
        <ul className="flex flex-col gap-2">
          {transactions.map(txn => {
            const { color, icon, label } = getTxnTypeInfo(txn.type);
            const { stock, category, party } = findDetails(txn);
            return (
              <li id={`txn-item-${txn.id}`} key={txn.id}
                className={`rounded-xl border border-[#e0e6f9] px-3 py-2 mb-1 cursor-pointer flex gap-2 items-center shadow font-sans transition-all duration-200 ${selectedTxnId === txn.id ? "ring-2 ring-[#8edbfc] bg-[#eafdff]" : "hover:bg-[#fbf7fe]"}`}
                onClick={() => onSelect({ transaction: txn, stock, category, party })} tabIndex={0}>
                <span className={"rounded-xl flex items-center justify-center w-10 h-10 mr-2 font-bold shadow-sm border " + color}>{icon}</span>
                <div className="flex-1 flex flex-col gap-[2px]">
                  <div className="text-[16px] font-bold text-[#2b4254]">{safeStr(party?.name)}</div>
                  <div className="text-[11.5px] text-[#879ab3] font-semibold">{txn.date} &bull; {label}</div>
                </div>
                <div className="ml-1 text-right">
                  <span className={"font-bold text-[15px] " + (txn.type === "buy" ? "text-[#12b386]" : txn.type === "sell" ? "text-[#f24464]" : "text-[#ba961b]")}>
                    {formatMoney(txn.amount, { symbol: true })}
                  </span>
                </div>
              </li>
            );
          })}
          {!transactions.length && <li className="text-center text-[#b3b9cf] font-semibold py-12">No transactions.</li>}
        </ul>
      </div>
      <span className="block px-6 py-3 text-xs text-[#6a8cbc] font-semibold border-t bg-[#e7f7ff]">
        <span className="font-bold text-[#3e7bbd]">Tip:</span> Tap a transaction to view details.
      </span>
    </aside>
  );
}

// ─── MAIN COMPONENT ───────────────────────────────────────────────────────────

function UserHistory() {
  const [allParties, setAllParties]       = useState<Party[]>([]);
  const [allStocks, setAllStocks]         = useState<Stock[]>([]);
  const [withdrawals, setWithdrawals]     = useState<Withdrawal[]>([]);
  const [stockHistory, setStockHistory]   = useState<StockHistory[]>([]);
  const [categories, setCategories]       = useState<Category[]>([]);
  const [sellTxnsRaw, setSellTxnsRaw]     = useState<any[]>([]);
  const [filters, setFilters]             = useState<FilterState>({});
  const [isLoading, setIsLoading]         = useState(true);
  const [sidePanelCollapsed, setSidePanelCollapsed] = useState(true);
  const [editTransactionModal, setEditTransactionModal] = useState<null | { transaction: Transaction; stock: Stock | undefined; category: Category | any; party: Party | undefined }>(null);
  const [selectedTransaction, setSelectedTransaction] = useState<{ transaction: Transaction; stock: Stock | undefined; category: Category | any; party: Party | undefined } | null>(null);
  const [selectedTxnPanel, setSelectedTxnPanel] = useState<string | number | null>(null);
  const [selectedFundDetail, setSelectedFundDetail] = useState<{ holding: AssetHolding; lotNumber: number } | null>(null);

  const location = useLocation();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const partyFromUrl = searchParams.get("party");
    const pathParts = location.pathname.split("/");
    const last = pathParts[pathParts.length - 1];
    const idFromPath = last && !isNaN(Number(last)) ? last : undefined;
    const newPartyId = partyFromUrl || idFromPath;
    setFilters(prev => {
      if (newPartyId && String(prev.party_id) !== String(newPartyId)) return { ...prev, party_id: newPartyId };
      if (!newPartyId && prev.party_id) { const cp = { ...prev }; delete cp.party_id; return cp; }
      return prev;
    });
  }, [location.search, location.pathname]);

  useEffect(() => {
    if (window.location.hash.startsWith("#q=")) {
      const quickKey = decodeURIComponent(window.location.hash.slice(3));
      if (QUICK_FILTERS.find(q => q.key === quickKey)) setFilters(prev => ({ ...prev, quick: quickKey, ...applyQuickFilter(quickKey) }));
    }
  }, []);

  async function fetchAllData() {
    setIsLoading(true);
    try {
      const [partiesRes, stocksRes, withdrawalRes, stockHistoryRes, categoriesRes, sellStocksRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/auth/addparty`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/addstocks`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/withdrawals`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/updatestock`),
        axios.get(`${process.env.REACT_APP_API_URL}/auth/categories`),
        axios.get(`https://stockaadvikbackend.aadviktech.com/auth/sellstocks`),
      ]);
      setAllParties(partiesRes.data || []);
      const stocksArr = (Array.isArray(stocksRes.data) ? stocksRes.data : []).map((stock: any) => ({
        ...stock,
        categories: (() => {
          try {
            if (Array.isArray(stock.categories)) return stock.categories;
            if (typeof stock.categories === "string" && stock.categories.length > 0) return JSON.parse(stock.categories);
            return [];
          } catch { return []; }
        })(),
      }));
      setAllStocks(stocksArr);
      setWithdrawals(withdrawalRes.data || []);
      setStockHistory(stockHistoryRes.data || []);
      setCategories(categoriesRes.data || []);
      setSellTxnsRaw(Array.isArray(sellStocksRes.data) ? sellStocksRes.data : []);
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); }
  }

  useEffect(() => { fetchAllData(); }, []);

  function handleQuickFilter(key: string) {
    setFilters(prev => ({ ...prev, ...applyQuickFilter(key), quick: key }));
    window.location.hash = "#q=" + encodeURIComponent(key);
  }
  function clearQuickFilter() {
    setFilters(prev => { const { quick, ...rest } = prev; return rest; });
    window.location.hash = "";
  }

  // ── Build transactions ──
  const buyTransactions: Transaction[] = [];
  allStocks.forEach(stock => {
    if (!Array.isArray(stock.categories)) return;
    stock.categories.forEach((cat: any) => {
      const qty = typeof cat.unit === "number" ? cat.unit : Number(cat.unit) || 0;
      buyTransactions.push({
        id: `buy_${stock.id}_${cat.id ?? cat.category_name}`,
        party_id: stock.party_id ?? "",
        date: stock.date?.slice(0, 10) || "",
        investment_type: cat.investment_type || "structure",
        type: "buy",
        stock_id: stock.id,
        lot_id: stock.id,
        category_id: cat.id ?? cat.category_name ?? cat,
        qty: isNaN(qty) ? 1 : qty,
        unit: safeStr(cat.unit),
        amount: (Number(cat.total_amount) || 0) + (cat.indirect_charges != null ? Number(cat.indirect_charges) || 0 : 0),
        remark: "Stock added",
      });
    });
  });

  const withdrawalTransactions: Transaction[] = [];
  withdrawals.forEach(wd => {
    let cats: any[] = [];
    try {
      const firstPass = JSON.parse(wd.catrgories || "[]");
      cats = Array.isArray(firstPass) && typeof firstPass[0] === "string" ? JSON.parse(firstPass[0]) : firstPass;
    } catch { cats = []; }
    if (cats.length === 0) {
      withdrawalTransactions.push({ id: `withdraw_${wd.id}`, party_id: wd.party_id, date: wd.date?.slice(0, 10) || "", type: "withdraw", stock_id: undefined, category_id: undefined, qty: 1, unit: "-", amount: Number(wd.amount) || 0, remark: "Withdrawal" });
      return;
    }
    cats.forEach((c: any, i: number) => {
      withdrawalTransactions.push({
        id: `withdraw_${wd.id}_${i}`, party_id: wd.party_id, date: wd.date?.slice(0, 10) || "", type: "withdraw",
        investment_type: "structure", stock_id: c.stockName || c.stock_name, category_id: c.category || c.category_name,
        qty: 1, unit: "-", amount: Number(c.amount || wd.amount) / (cats.length || 1), remark: "Withdrawal",
      });
    });
  });

  const sellTransactions: Transaction[] = useMemo(() => {
    return (sellTxnsRaw || []).map(sell => ({
      id: `sell_${sell.id}`, party_id: sell.party_id, date: sell.date?.slice(0, 10) ?? "",
      type: "sell" as TransactionType, investment_type: sell.investment_type || "structure",
      stock_id: sell.stock_name || sell.stock_id, category_id: sell.category,
      qty: typeof sell.sell_units === "string" ? parseFloat(sell.sell_units) : (sell.sell_units ?? 0),
      unit: sell.unit || "units",
      amount: typeof sell.sell_amount === "string" ? parseFloat(sell.sell_amount) : (sell.sell_amount ?? 0),
      remark: sell.remark ?? "Sell",
      indirect_charges: sell.indirect_charges !== undefined ? Number(sell.indirect_charges) : undefined,
    }));
  }, [sellTxnsRaw]);

  const allTransactionsRaw: Transaction[] = [
    ...buyTransactions, ...withdrawalTransactions, ...sellTransactions,
  ].map(t => ({ ...t, unit: safeStr(t.unit) }));

  const findStockAndCategory = useCallback((stock_identifier: string | number | undefined, category_id: string | number | undefined) => {
    const stock = allStocks.find(s => String(s.id) === String(stock_identifier) || s.categories.some((cat: any) => cat.stock_name === stock_identifier));
    let category: any = null;
    if (stock && Array.isArray(stock.categories)) {
      category = stock.categories.find((cat: any) => String(cat.id ?? cat.category_name) === String(category_id) || cat.stock_name === stock_identifier);
    }
    if (!category && category_id) category = categories.find(c => String(c.id) === String(category_id) || c.category_name === category_id);
    if (!category && category_id) category = { category_name: category_id };
    return { stock, category };
  }, [allStocks, categories]);

  const filteredTransactions = useMemo(() => {
    return allTransactionsRaw.filter(t => {
      if (filters.party_id && String(t.party_id) !== String(filters.party_id)) return false;
      if (filters.from && t.date < filters.from) return false;
      if (filters.to   && t.date > filters.to)   return false;
      if (filters.type && t.type !== filters.type) return false;
      if (filters.stock_id && String(t.stock_id) !== String(filters.stock_id)) return false;
      if (filters.category_id) {
        const filterCat = String(filters.category_id).toLowerCase().trim();
        const rawCatId  = String(t.category_id ?? "").toLowerCase().trim();
        const { category } = findStockAndCategory(t.stock_id, t.category_id);
        const resolvedName = (safeStr(category?.category_name) !== "-" ? safeStr(category?.category_name) : safeStr(category?.category) !== "-" ? safeStr(category?.category) : safeStr(category?.stock_name) !== "-" ? safeStr(category?.stock_name) : rawCatId).toLowerCase().trim();
        if (resolvedName !== filterCat && rawCatId !== filterCat) return false;
      }
      const amt = Number(t.amount);
      if (filters.minAmount && (isNaN(amt) || amt < Number(filters.minAmount))) return false;
      if (filters.maxAmount && (isNaN(amt) || amt > Number(filters.maxAmount))) return false;
      if (filters.search) {
        const q = filters.search.toLowerCase();
        const party = allParties.find(p => String(p.id) === String(t.party_id));
        const matched = String(t.id).toLowerCase().includes(q) || String(t.remark ?? "").toLowerCase().includes(q) || String(t.amount).includes(q) || String(t.unit ?? "").toLowerCase().includes(q) || safeStr(party?.name).toLowerCase().includes(q) || String(t.category_id ?? "").toLowerCase().includes(q) || String(t.stock_id ?? "").toLowerCase().includes(q);
        if (!matched) return false;
      }
      return true;
    }).sort((a, b) => a.date === b.date ? (String(a.id) < String(b.id) ? -1 : 1) : new Date(a.date).getTime() - new Date(b.date).getTime());
  }, [JSON.stringify(allTransactionsRaw), filters.from, filters.to, filters.type, filters.stock_id, filters.category_id, filters.party_id, filters.minAmount, filters.maxAmount, filters.search, findStockAndCategory, allParties]);

  const processedHoldings = useMemo(() => {
    const holdingsMap = new Map<string, AssetHolding>();
    filteredTransactions.filter(t => t.type === "buy").forEach(t => {
      const { stock, category } = findStockAndCategory(t.stock_id, t.category_id);
      const stockName = category?.stock_name || category?.stockName || stock?.id || String(t.stock_id || "-");
      const catName   = category?.category_name || category?.category || String(t.category_id || "-");
      const key = `${stockName}_${catName}_lot${t.lot_id ?? t.id}`.toLowerCase().trim();
      if (!holdingsMap.has(key)) {
        holdingsMap.set(key, { stock_id: t.stock_id, category_id: t.category_id, party_id: t.party_id, lot_id: t.lot_id, investment_type: t.investment_type, stock, category, total_bought: 0, total_withdraw: 0, total_sold: 0, remaining_qty: 0, unit: safeStr(t.unit), current_price: 0, holding_value: 0, history: [] });
      }
      const record = holdingsMap.get(key)!;
      const qty = parseFloat(String(t.qty)) || 0;
      record.total_bought += qty; record.remaining_qty += qty; record.history.push(t);
    });
    filteredTransactions.filter(t => t.type === "sell" || t.type === "withdraw").forEach(t => {
      const { stock, category } = findStockAndCategory(t.stock_id, t.category_id);
      const stockName = category?.stock_name || category?.stockName || stock?.id || String(t.stock_id || "-");
      const catName   = category?.category_name || category?.category || String(t.category_id || "-");
      const baseKey   = `${stockName}_${catName}`.toLowerCase().trim();
      const matchingLots = Array.from(holdingsMap.entries()).filter(([k]) => k.startsWith(baseKey + "_lot")).sort((a, b) => {
        const aDate = a[1].history.find(h => h.type === "buy")?.date || "";
        const bDate = b[1].history.find(h => h.type === "buy")?.date || "";
        return aDate.localeCompare(bDate);
      });
      let remaining = parseFloat(String(t.qty)) || 0;
      for (const [, lotRecord] of matchingLots) {
        if (remaining <= 0) break;
        const available = lotRecord.remaining_qty;
        if (available <= 0) continue;
        const toDeduct = Math.min(remaining, available);
        remaining -= toDeduct;
        if (t.type === "sell")     { lotRecord.total_sold     += toDeduct; lotRecord.remaining_qty -= toDeduct; }
        if (t.type === "withdraw") { lotRecord.total_withdraw += toDeduct; lotRecord.remaining_qty -= toDeduct; }
        lotRecord.history.push({ ...t, qty: toDeduct });
      }
      if (matchingLots.length === 0) {
        const fallbackKey = `${baseKey}_lot_orphan_${t.id}`.toLowerCase().trim();
        holdingsMap.set(fallbackKey, { stock_id: t.stock_id, category_id: t.category_id, party_id: t.party_id, lot_id: undefined, investment_type: t.investment_type, stock, category, total_bought: 0, total_withdraw: t.type === "withdraw" ? (parseFloat(String(t.qty)) || 0) : 0, total_sold: t.type === "sell" ? (parseFloat(String(t.qty)) || 0) : 0, remaining_qty: 0, unit: safeStr(t.unit), current_price: 0, holding_value: 0, history: [t] });
      }
    });
    holdingsMap.forEach(record => {
      const latestPrice = stockHistory.filter(h => String(h.stock_id) === String(record.stock_id)).sort((a, b) => new Date(b.current_price_date).getTime() - new Date(a.current_price_date).getTime())[0];
      let cPrice = latestPrice ? (parseFloat(String(latestPrice.current_price)) || 0) : 0;
      if (!cPrice) cPrice = record.category?.total_amount / (record.category?.unit || 1) || 0;
      const buys = record.history.filter(tr => tr.type === "buy");
      const totalSpent = buys.reduce((s, tr) => s + (Number(tr.amount) || 0), 0);
      const totalQty   = buys.reduce((s, tr) => s + (Number(tr.qty) || 0), 0);
      record.current_price  = cPrice;
      record.avg_buy_price  = totalQty > 0 ? totalSpent / totalQty : 0;
      record.holding_value  = Math.max(0, record.remaining_qty) * cPrice;
    });
    return holdingsMap;
  }, [filteredTransactions, stockHistory, allStocks]);

  function getLotNumber(a: AssetHolding): number {
    const aS = safeStr(a.category?.stock_name || a.category?.stockName || a.stock?.id || a.stock_id);
    const aC = safeStr(a.category?.category_name || a.category?.category || a.category_id);
    const siblings = Array.from(processedHoldings.values()).filter(h => {
      const hS = safeStr(h.category?.stock_name || h.category?.stockName || h.stock?.id || h.stock_id);
      const hC = safeStr(h.category?.category_name || h.category?.category || h.category_id);
      return hS === aS && hC === aC;
    }).sort((x, y) => {
      const xD = x.history.find(t => t.type === "buy")?.date || "";
      const yD = y.history.find(t => t.type === "buy")?.date || "";
      return xD.localeCompare(yD);
    });
    const idx = siblings.findIndex(h => h.stock_id === a.stock_id && h.lot_id === a.lot_id);
    return idx >= 0 ? idx + 1 : 1;
  }

  const stats = useMemo(() => {
    let invested = 0, withdrawn = 0, sold = 0, balance = 0;
    filteredTransactions.forEach(t => {
      const amt = parseFloat(String(t.amount)) || 0;
      if (t.type === "buy")      invested  += amt;
      if (t.type === "withdraw") withdrawn += amt;
      if (t.type === "sell")     sold      += amt;
    });
    processedHoldings.forEach(r => { if (r.holding_value > 0) balance += r.holding_value; });
    return { invested, withdrawn, sold, balance };
  }, [filteredTransactions, processedHoldings]);

  const percentReturn = stats.invested ? (((stats.balance + stats.withdrawn + stats.sold - stats.invested) / stats.invested) * 100).toFixed(2) : "0.00";

  const statDetails = [
    { icon: <IoWalletSharp className="text-[#F49A40] text-2xl" />, label: "Total Invested",   value: stats.invested,  color: "#fffee0", sub: "All buy amount." },
    { icon: <FaRegArrowAltCircleDown className="text-[#29b87f] text-2xl" />, label: "Withdrawn", value: stats.withdrawn, color: "#e7fbe8", sub: "All withdrawals." },
    { icon: <FaRegArrowAltCircleUp className="text-[#ed0e25] text-2xl" />, label: "Amount Sold",  value: stats.sold,     color: "#ffe3e7", sub: "Total sell value." },
    { icon: <GiWallet className="text-[#319F68] text-2xl" />, label: "Current Value",  value: stats.balance,  color: "#e1efed", sub: "Current value of holdings." },
    { icon: <AiOutlineFundProjectionScreen className="text-[#5567ef] text-2xl" />, label: "Return %", value: `${Number(percentReturn) >= 0 ? "+" : ""}${percentReturn}%`, color: Number(percentReturn) >= 0 ? "#f0fdf4" : "#fff1f2", sub: "Overall P&L %" },
  ];

  async function handleDeleteTransaction(id: any) {
    if (!id) return;
    const parts = String(id).split("_");
    const tag = parts[0]; const rawId = parts[1];
    if (!tag || !rawId) { alert("Invalid transaction ID format."); return; }
    if (!window.confirm("Are you sure you want to delete this transaction?")) return;
    try {
      if (tag === "buy")      await axios.delete(`${process.env.REACT_APP_API_URL}/auth/addstocks`, { data: { id: rawId } });
      if (tag === "sell")     await axios.delete(`${process.env.REACT_APP_API_URL}/auth/sellstocks/${rawId}`);
      if (tag === "withdraw") await axios.delete(`${process.env.REACT_APP_API_URL}/auth/withdrawals/${rawId}`);
      await fetchAllData();
    } catch (err: any) { alert(err?.response?.data?.message || err?.message || "Failed to delete transaction."); }
  }

  async function handleSaveEditedTransaction(edited: Transaction) {
    setIsLoading(true);
    try {
      if (String(edited.id).startsWith("buy_")) {
        const stockId = edited.stock_id;
        const stocksRes = await axios.get(`${process.env.REACT_APP_API_URL}/auth/addstocks`);
        const stocksArr = (Array.isArray(stocksRes.data) ? stocksRes.data : []).map((s: any) => ({ ...s, categories: (() => { try { return Array.isArray(s.categories) ? s.categories : JSON.parse(s.categories || "[]"); } catch { return []; } })() }));
        const stockOriginal = stocksArr.find((s: any) => String(s.id) === String(stockId));
        if (stockOriginal) {
          const updatedCategories = stockOriginal.categories.map((cat: any) => String(cat.id ?? cat.category_name ?? cat) === String(edited.category_id) ? { ...cat, qty: edited.qty, unit: edited.unit, total_amount: edited.amount, date: edited.date?.substring(0, 10) } : cat);
          await axios.put(`${process.env.REACT_APP_API_URL}/auth/addstocks`, { ...stockOriginal, categories: updatedCategories, date: edited.date?.substring(0, 10) });
        }
      } else if (String(edited.id).startsWith("sell_")) {
        const sellId = String(edited.id).replace(/^sell_/, "");
        const freshSells = await axios.get(`https://stockaadvikbackend.aadviktech.com/auth/sellstocks`);
        const existing = (Array.isArray(freshSells.data) ? freshSells.data : []).find((s: any) => String(s.id) === String(sellId));
        await axios.put(`https://stockaadvikbackend.aadviktech.com/auth/sellstocks/${sellId}`, { ...existing, id: sellId, party_id: edited.party_id, date: edited.date, stock_id: edited.stock_id, stock_name: edited.stock_id, category: edited.category_id, sell_units: edited.qty, sell_amount: edited.amount, indirect_charges: edited.indirect_charges });
      } else if (String(edited.id).startsWith("withdraw_")) {
        const withdrawalId = String(edited.id).split("_")[1];
        const allWithdrawals = await axios.get(`${process.env.REACT_APP_API_URL}/auth/withdrawals`);
        const existing = (Array.isArray(allWithdrawals.data) ? allWithdrawals.data : []).find((w: any) => String(w.id) === String(withdrawalId));
        await axios.put(`${process.env.REACT_APP_API_URL}/auth/withdrawals/${withdrawalId}`, { ...existing, id: withdrawalId, party_id: edited.party_id, date: edited.date, amount: edited.amount, catrgories: JSON.stringify([edited.category_id]), remark: edited.remark ?? "Withdrawal" });
      }
      await fetchAllData(); setEditTransactionModal(null);
    } catch (error) { throw error; }
    finally { setIsLoading(false); }
  }

  const tableSum = useMemo(() => filteredTransactions.reduce((acc, t) => ({ qty: acc.qty + t.qty, amount: acc.amount + (t.amount ?? 0) }), { qty: 0, amount: 0 }), [filteredTransactions]);
  const recentTxnKeys = useMemo(() => new Set(filteredTransactions.filter(tr => isRecent(tr.date)).map(tr => `${tr.stock_id || "-"}_${tr.category_id || "-"}`)), [filteredTransactions]);

  const [visibleCols, setVisibleCols] = useState({
    date: true, stock: true, category: true, party: true,
    type: true, investment_type: true, qty: true, unit: true,
    amount: true, remark: true, avg_buy_price: false,
  });

  function getYearlySummary() {
    const summary: { [year: string]: number } = {};
    filteredTransactions.forEach(t => { const year = t.date?.slice(0, 4) ?? ""; if (year) summary[year] = (summary[year] || 0) + (t.amount ?? 0); });
    return summary;
  }

  const barData = {
    labels: Array.from(processedHoldings.values()).map(a => safeStr(a.category?.stock_name || a.category?.category_name || a.category?.category || a.stock_id).slice(0, 14)),
    datasets: [{ label: "Holding Value", data: Array.from(processedHoldings.values()).map(a => a.holding_value), backgroundColor: "#74C0FC" }],
  };

  return (
    <div className="bg-gradient-to-bl from-[#e8f0fe] to-[#eaefec] min-h-screen flex flex-col font-sans relative">

      {selectedFundDetail && (
        <FundDetailPanel
          holding={selectedFundDetail.holding}
          allParties={allParties}
          stockHistory={stockHistory}
          lotNumber={selectedFundDetail.lotNumber}
          onClose={() => setSelectedFundDetail(null)}
        />
      )}

      <header className="w-full flex justify-between items-center px-8 py-5 border-b bg-white shadow-sm sticky top-0 z-40">
        <div className="text-2xl tracking-tight font-bold text-[#274472] flex items-center gap-3">
          <AiOutlineFundProjectionScreen className="text-blue-400" /> Asset Investment Tracker
        </div>
        <div className="flex gap-2">
          <button className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 border text-[#333] shadow text-sm" onClick={() => downloadCSV(filteredTransactions, allParties, "csv")}><FaDownload className="text-base" /> Export CSV</button>
          <button className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 border text-[#333] shadow text-sm" onClick={() => downloadCSV(filteredTransactions, allParties, "excel")}><FaDownload className="text-base" /> Excel</button>
          <button className="flex items-center gap-1 px-3 py-2 bg-[#f3f7fa] rounded hover:bg-blue-50 border text-[#333] shadow text-sm" onClick={() => window.print()}><MdPrint className="text-lg" /> Print</button>
        </div>
      </header>

      <div className="flex relative">
        <aside className={`fixed top-0 right-0 h-full bg-white shadow-lg z-40 transition-all duration-300 flex flex-col ${sidePanelCollapsed ? "w-14" : "w-[350px]"}`} style={{ minWidth: sidePanelCollapsed ? 56 : 350 }}>
          <button className="absolute top-4 left-0 -translate-x-full bg-[#e4eaff] hover:bg-[#d2dbfc] text-[#4175EB] border border-[#cfdafe] rounded-r-lg shadow px-2 py-1 z-50"
            onClick={() => setSidePanelCollapsed(v => !v)} aria-label={sidePanelCollapsed ? "Expand" : "Collapse"}>
            <svg className="h-6 w-6" fill="none" stroke="#4466e7" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d={sidePanelCollapsed ? "M9 5l7 7-7 7" : "M15 19l-7-7 7-7"} />
            </svg>
          </button>
          {!sidePanelCollapsed && (
            <div className="flex-1 overflow-y-auto">
              <PhonePeSidePanel transactions={filteredTransactions} allStocks={allStocks} allParties={allParties} categories={categories}
                onSelect={({ transaction, stock, category, party }) => { setSelectedTransaction({ transaction, stock, category, party }); setSelectedTxnPanel(transaction.id); }}
                selectedTxnId={selectedTxnPanel} />
            </div>
          )}
        </aside>

        <main className={`flex-1 w-full px-4 sm:px-10 lg:px-16 py-3 grid gap-7 transition-all duration-300 ${sidePanelCollapsed ? "mr-14" : "mr-[350px]"}`}>

          <section className="flex flex-wrap items-center gap-2 mb-2">
            <span className="text-[13px] text-[#4897ad] font-semibold mr-2 flex items-center gap-1"><TbHistory /> Quick Filters:</span>
            {QUICK_FILTERS.map(q => (
              <button key={q.key} onClick={() => handleQuickFilter(q.key)}
                className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition ${filters.quick === q.key ? "bg-[#63dbff]/20 border-[#3296bd] text-[#197995]" : "bg-[#f4fafd] border-[#d3e4f2] text-[#558cb3] hover:bg-[#e6f4ff]"}`}>
                {q.label}
              </button>
            ))}
            {filters.quick && <button className="ml-2 px-2 py-1 rounded bg-white text-[#888] border border-[#ecd5ff] hover:bg-[#fde6ff] text-xs font-semibold" onClick={clearQuickFilter}>Clear Filter</button>}
          </section>

          <AdvancedFilters filters={filters} setFilters={setFilters} allParties={allParties} allStocks={allStocks} allCategories={categories} />

          {(filters.party_id || filters.type || filters.category_id || filters.from || filters.to || filters.minAmount || filters.maxAmount || filters.search) && (
            <div className="flex flex-wrap gap-2 -mt-4 mb-2">
              {filters.party_id && (() => { const p = allParties.find(p => String(p.id) === String(filters.party_id)); return <span className="px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-xs font-bold flex items-center gap-1">Party: {safeStr(p?.name) !== "-" ? safeStr(p?.name) : filters.party_id} <button onClick={() => setFilters(f => { const n = {...f}; delete n.party_id; return n; })} className="ml-1 text-blue-400 hover:text-blue-700">×</button></span>; })()}
              {filters.type && <span className="px-3 py-1 rounded-full bg-purple-100 text-purple-800 text-xs font-bold flex items-center gap-1">Type: {filters.type} <button onClick={() => setFilters(f => { const n = {...f}; delete n.type; return n; })} className="ml-1 text-purple-400 hover:text-purple-700">×</button></span>}
              {filters.category_id && <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-bold flex items-center gap-1">Category: {filters.category_id} <button onClick={() => setFilters(f => { const n = {...f}; delete n.category_id; return n; })} className="ml-1 text-amber-400 hover:text-amber-700">×</button></span>}
              {(filters.from || filters.to) && <span className="px-3 py-1 rounded-full bg-green-100 text-green-800 text-xs font-bold">{filters.from || "…"} → {filters.to || "…"}</span>}
              <span className="text-xs text-slate-400 flex items-center">{filteredTransactions.length} result{filteredTransactions.length !== 1 ? "s" : ""}</span>
            </div>
          )}

          <section className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
            {statDetails.map((d, idx) => (
              <div key={idx} className="rounded-xl flex flex-col gap-3 px-6 py-6 shadow relative border" style={{ background: d.color, minHeight: 130, justifyContent: "center" }}>
                <div className="flex items-center gap-3">
                  <div className="bg-white shadow rounded-full w-11 h-11 flex justify-center items-center border">{d.icon}</div>
                  <div>
                    <div className="font-bold text-lg text-[#18263a]">{typeof d.value === "string" ? d.value : formatMoney(d.value, { symbol: true })}</div>
                    <div className="text-xs font-semibold text-[#758ba7]">{d.label}</div>
                    <div className="text-[10px] text-[#b4bfce]">{d.sub}</div>
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Holdings Table */}
          <section className="bg-white rounded-xl shadow p-6 mb-10">
            <div className="font-bold text-[#3F4765] text-xl mb-6 flex items-center gap-2">
              <GiWallet className="text-[#4883e9]/60" /> Current Asset Holdings
              <span className="text-xs ml-2 text-[#7a9ac3]">({processedHoldings.size} lots)</span>
              <span className="ml-auto text-xs text-slate-400 font-normal">Each row = one purchase lot · Click "Detail" for full breakdown</span>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-[1100px] w-full text-xs border-separate border-spacing-y-1">
                <thead>
                  <tr className="bg-[#F7F8FA] border-b">
                    <th className="p-2 text-left text-[#3b4d6f] font-bold">Stock</th>
                    <th className="p-2 text-left text-[#3b4d6f] font-bold">Category</th>
                    <th className="p-2 text-center text-[#3b4d6f] font-bold">Lot</th>
                    <th className="p-2 text-left text-[#3b4d6f] font-bold">Buy Date</th>
                    <th className="p-2 text-left font-bold">Inv. Type</th>
                    <th className="p-2 text-right font-bold">Total Bought</th>
                    <th className="p-2 text-right font-bold">Sold</th>
                    <th className="p-2 text-right font-bold">Remaining</th>
                    <th className="p-2 text-right font-bold">Current Price</th>
                    <th className="p-2 text-right font-bold">Avg Buy</th>
                    <th className="p-2 text-right font-bold">Holding Value</th>
                    <th className="p-2 text-center font-bold">Trend</th>
                    <th className="p-2 text-center font-bold">Fund Detail</th>
                  </tr>
                </thead>
                <tbody>
                  {Array.from(processedHoldings.values()).map((a, idx) => {
                    const key = `${a.stock_id || "-"}_${a.category_id || "-"}_${a.lot_id ?? idx}`;
                    const isNew = recentTxnKeys.has(`${a.stock_id || "-"}_${a.category_id || "-"}`);
                    const lotNumber = getLotNumber(a);
                    const stockDisplayName = (() => {
                      if (Array.isArray(a.category)) return safeStr(a.category[0]?.stock_name);
                      if (a.category && typeof a.category === "object") return safeStr(a.category.stockName || a.category.stock_name || a.category.stockId || a.stock?.id);
                      return safeStr(a.stock?.id);
                    })();
                    const catDisplayName = safeStr(a.category?.category_name) !== "-" ? safeStr(a.category?.category_name) : safeStr(a.category?.category) !== "-" ? safeStr(a.category?.category) : safeStr(a.category?.stock_name) !== "-" ? safeStr(a.category?.stock_name) : safeStr(a.category_id);
                    const firstBuyDate = a.history.filter(t => t.type === "buy").sort((x, y) => new Date(x.date).getTime() - new Date(y.date).getTime())[0]?.date || "—";
                    return (
                      <tr key={key} className={(isNew ? "bg-yellow-50 ring-1 ring-yellow-300 " : "") + (idx % 2 === 0 ? "bg-[#f6fafb]" : "bg-white")}>
                        <td className="p-2">
                          <Link className="underline text-[#226C92] font-semibold" to="/unified-balance-sheet">{stockDisplayName}</Link>
                          {isNew && <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] font-bold bg-yellow-300 text-yellow-900 animate-pulse">NEW</span>}
                        </td>
                        <td className="p-2">{catDisplayName}</td>
                        <td className="p-2 text-center">
                          <span className="px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-black tracking-wide">Lot {lotNumber}</span>
                        </td>
                        <td className="p-2 text-[11px] font-semibold text-slate-500">{firstBuyDate}</td>
                        <td className="p-2">
                          <span className={clsx("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider", a.investment_type === "direct" ? "bg-blue-100 text-blue-700" : a.investment_type === "structure" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600")}>
                            {a.investment_type || "-"}
                          </span>
                        </td>
                        <td className="p-2 text-right font-bold text-[#16a34a]">{a.total_bought.toFixed(4)}</td>
                        <td className="p-2 text-right font-bold text-[#ef4444]">{a.total_sold.toFixed(4)}</td>
                        <td className="p-2 text-right font-semibold text-[#11977D]">{a.remaining_qty.toFixed(4)}</td>
                        <td className="p-2 text-right">{formatMoney(a.current_price, { symbol: true })}</td>
                        <td className="p-2 text-right text-[#5751d6]">{formatMoney(a.avg_buy_price ?? 0, { symbol: true })}</td>
                        <td className="p-2 text-right font-bold text-[#1457f1]">{formatMoney(a.holding_value, { symbol: true })}</td>
                        <td className="p-2 text-center">
                          <div style={{ width: 68, height: 24, display: "inline-block" }}>
                            <Line
                              data={{ labels: a.history.map((_, i) => i + 1), datasets: [{ label: "Qty", data: a.history.map(tr => tr.qty), backgroundColor: "#93DEFF99", borderColor: "#19a0fa", pointRadius: 0, tension: 0.5 }] }}
                              options={{ responsive: false, plugins: { legend: { display: false } }, scales: { x: { display: false }, y: { display: false } }, elements: { line: { borderWidth: 2 }, point: { radius: 1 } } }}
                              height={24} width={68}
                            />
                          </div>
                        </td>
                        <td className="p-2 text-center">
                          <button onClick={() => setSelectedFundDetail({ holding: a, lotNumber })}
                            className="px-3 py-1 rounded-lg bg-gradient-to-r from-indigo-500 to-blue-500 text-white text-[11px] font-bold shadow hover:from-indigo-600 hover:to-blue-600 transition whitespace-nowrap">
                            📊 Detail
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {processedHoldings.size === 0 && <tr><td colSpan={13} className="p-4 text-center text-[#9CA3AF]">No asset holdings found.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          {/* Transactions Ledger */}
          <section className="bg-white rounded-xl shadow p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <span className="font-bold text-[#3F4765] text-xl flex items-center gap-2">
                <FaExchangeAlt className="text-[#357fb2]" /> All Transactions Ledger
                <span className="text-xs text-[#7a9ac3]">({filteredTransactions.length} entries)</span>
              </span>
              <div className="flex gap-3 flex-wrap">
                <label className="flex items-center gap-1 text-xs font-semibold text-[#3F4765]">
                  Columns:
                  {(Object.keys(visibleCols) as Array<keyof typeof visibleCols>).map(col => (
                    <span key={col} className="flex items-center gap-1 ml-2">
                      <input type="checkbox" checked={visibleCols[col]} onChange={() => setVisibleCols(prev => ({ ...prev, [col]: !prev[col] }))} />
                      {col.replace(/_/g, " ").replace(/\b\w/g, ch => ch.toUpperCase())}
                    </span>
                  ))}
                </label>
              </div>
            </div>
            <div className="rounded overflow-x-auto">
              <table className="min-w-[950px] w-full text-xs border-separate border-spacing-y-1">
                <thead>
                  <tr className="bg-[#f3f7fa] border-b text-[#42547b]">
                    {visibleCols.date            && <th className="p-2">Date</th>}
                    {visibleCols.stock           && <th className="p-2">Stock</th>}
                    {visibleCols.investment_type && <th className="p-2">Inv. Type</th>}
                    {visibleCols.category        && <th className="p-2">Category</th>}
                    {visibleCols.party           && <th className="p-2">Party</th>}
                    {visibleCols.type            && <th className="p-2">Type</th>}
                    {visibleCols.qty             && <th className="p-2">Qty</th>}
                    {visibleCols.unit            && <th className="p-2">Unit</th>}
                    {visibleCols.amount          && <th className="p-2">Amount (₹)</th>}
                    {visibleCols.avg_buy_price   && <th className="p-2">Avg Buy</th>}
                    {visibleCols.remark          && <th className="p-2">Remark</th>}
                    <th className="p-2">Detail</th>
                    <th className="p-2">Edit</th>
                    <th className="p-2">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredTransactions.length ? filteredTransactions.map((t, idx) => {
                    const { stock, category } = findStockAndCategory(t.stock_id, t.category_id);
                    const party = allParties.find(p => String(p.id) === String(t.party_id));
                    const isNewTxn = isRecent(t.date);
                    const catDisplayName = safeStr(category?.category_name) !== "-" ? safeStr(category?.category_name) : safeStr(category?.category) !== "-" ? safeStr(category?.category) : safeStr(category?.stock_name) !== "-" ? safeStr(category?.stock_name) : safeStr(t.category_id);
                    return (
                      <tr key={t.id} className={(isNewTxn ? "bg-yellow-50 ring-1 ring-yellow-300 " : "") + (idx % 2 === 0 ? "bg-[#ecf7fb]" : "bg-[#fbfbfd]")}>
                        {visibleCols.date && (
                          <td className="p-2 whitespace-nowrap">
                            {t.date}
                            {isNewTxn && <span className="ml-2 px-1.5 py-0.5 rounded-full bg-yellow-300 text-yellow-900 text-[10px] font-extrabold animate-pulse">NEW</span>}
                          </td>
                        )}
                        {visibleCols.stock           && <td className="p-2">{safeStr(category?.stock_name) !== "-" ? safeStr(category?.stock_name) : safeStr(t.stock_id)}</td>}
                        {visibleCols.investment_type && (
                          <td className="p-2">
                            <span className={clsx("px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider", t.investment_type === "direct" ? "bg-blue-100 text-blue-700" : t.investment_type === "structure" ? "bg-purple-100 text-purple-700" : "bg-gray-100 text-gray-600")}>
                              {t.investment_type || "-"}
                            </span>
                          </td>
                        )}
                        {visibleCols.category      && <td className="p-2">{catDisplayName}</td>}
                        {visibleCols.party         && <td className="p-2">{safeStr(party?.name) !== "-" ? safeStr(party?.name) : safeStr(t.party_id)}</td>}
                        {visibleCols.type          && (
                          <td className="p-2">
                            {t.type === "buy"      && <span className="inline-flex items-center gap-1 text-[#36a859] font-bold"><BsBoxArrowInDown /> Buy</span>}
                            {t.type === "sell"     && <span className="inline-flex items-center gap-1 text-[#F04959] font-bold"><BsBoxArrowUpRight /> Sell</span>}
                            {t.type === "withdraw" && <span className="inline-flex items-center gap-1 text-[#c87b12] font-bold"><TbZoomMoney /> Withdraw</span>}
                          </td>
                        )}
                        {visibleCols.qty    && <td className="p-2">{t.qty}</td>}
                        {visibleCols.unit   && <td className="p-2">{safeStr(t.unit)}</td>}
                        {visibleCols.amount && <td className="p-2 font-semibold text-[#336995]">{Number(t.amount).toLocaleString("en-IN", { maximumFractionDigits: 2 })}</td>}
                        {visibleCols.avg_buy_price && (
                          <td className="p-2 font-semibold text-[#856cdb]">
                            {(() => {
                              const { stock: s, category: c } = findStockAndCategory(t.stock_id, t.category_id);
                              const sN = c?.stock_name || c?.stockName || s?.id || String(t.stock_id || "-");
                              const cN = c?.category_name || c?.category || String(t.category_id || "-");
                              const lP = t.lot_id != null ? `_lot${t.lot_id}` : "";
                              const k = `${sN}_${cN}${lP}`.toLowerCase().trim();
                              const ah = processedHoldings.get(k);
                              return ah?.avg_buy_price ? formatMoney(ah.avg_buy_price, { symbol: true }) : "-";
                            })()}
                          </td>
                        )}
                        {visibleCols.remark && <td className="p-2 text-[#717E98]">{safeStr(t.remark)}</td>}
                        <td className="p-2 text-center">
                          <button className="text-blue-500 hover:underline text-xs font-semibold" onClick={() => { setSelectedTransaction({ transaction: t, stock, category, party }); setSelectedTxnPanel(t.id); }}>Detail</button>
                        </td>
                        <td className="p-2 text-center">
                          <button className="text-yellow-600 hover:underline text-xs font-semibold flex items-center gap-1"
                            onClick={() => { const ep = t.type === "buy" ? "edit-stock" : t.type === "sell" ? "edit-sell" : "edit-widthsrawl"; window.location.href = `/${ep}/?party_id=${t.party_id}&category=${t.category_id}&stock_id=${t.stock_id}`; }}>
                            <FaRegEdit className="inline" /> Edit
                          </button>
                        </td>
                        <td className="p-2 text-center">
                          <button className="text-red-600 hover:bg-red-50 text-xs font-semibold flex items-center justify-center gap-1 px-3 py-1 rounded border border-transparent hover:border-red-200" onClick={() => handleDeleteTransaction(t.id)}>
                            🗑 Delete
                          </button>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr><td colSpan={Object.values(visibleCols).filter(Boolean).length + 3} className="p-6 text-center text-[#B1B5C4] text-sm">No transactions found</td></tr>
                  )}
                </tbody>
                {filteredTransactions.length > 0 && (
                  <tfoot>
                    <tr className="bg-[#ecf7fb] font-bold text-[#436095] border-t">
                      {visibleCols.date            && <td className="p-2 text-right">Total</td>}
                      {visibleCols.stock           && <td></td>}
                      {visibleCols.investment_type && <td></td>}
                      {visibleCols.category        && <td></td>}
                      {visibleCols.party           && <td></td>}
                      {visibleCols.type            && <td></td>}
                      {visibleCols.qty             && <td className="p-2">{tableSum.qty.toFixed(2)}</td>}
                      {visibleCols.unit            && <td></td>}
                      {visibleCols.amount          && <td className="p-2 text-[#218758]">{formatMoney(tableSum.amount, { symbol: true })}</td>}
                      {visibleCols.avg_buy_price   && <td></td>}
                      {visibleCols.remark          && <td></td>}
                      <td></td><td></td><td></td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </section>

          {/* Charts */}
          <section className="grid grid-cols-1 lg:grid-cols-5 gap-10 mt-8 mb-4">
            <div className="relative bg-gradient-to-tl from-[#fff3ec] via-[#e4efff] to-[#f1f8ff] shadow-xl rounded-3xl p-8 flex flex-col items-center col-span-2 border-2 border-[#e9e9fc]">
              <div className="mb-6 flex items-center gap-2">
                <FaChartPie className="w-7 h-7 text-[#8357ff]" />
                <span className="font-extrabold text-transparent text-lg bg-clip-text bg-gradient-to-r from-[#633fef] to-[#7ae883]">Holdings by Category</span>
              </div>
              <div className="w-[220px] h-[220px]">
                <Doughnut
                  data={{
                    labels: Array.from(processedHoldings.values()).map(a => { const base = safeStr(a.category?.category_name) !== "-" ? safeStr(a.category?.category_name) : safeStr(a.category?.category) !== "-" ? safeStr(a.category?.category) : safeStr(a.category_id); return `${base} (Lot ${getLotNumber(a)})`; }),
                    datasets: [{ data: Array.from(processedHoldings.values()).map(a => a.holding_value), backgroundColor: ["rgba(134,85,255,0.98)","rgba(24,189,202,0.9)","rgba(255,174,63,0.95)","rgba(80,216,255,0.88)","rgba(253,238,117,0.85)","rgba(255,124,163,0.92)","rgba(70,201,156,0.91)","rgba(249,100,67,0.93)"], borderWidth: 4, borderColor: "#fff", hoverOffset: 10 }],
                  }}
                  options={{ plugins: { legend: { position: "bottom", labels: { color: "#5c6698", font: { size: 12, weight: "bold" }, boxWidth: 18, padding: 14, usePointStyle: true } } }, cutout: 82 }}
                />
              </div>
            </div>

            <div className="relative bg-gradient-to-b from-[#e1f2ff] via-[#fffdf9] to-[#d3f6ed] shadow-2xl rounded-3xl p-8 flex flex-col items-center col-span-3 border-2 border-[#e4eaff]">
              <div className="mb-6 flex items-center gap-2">
                <AiOutlineFundProjectionScreen className="text-[#41cee8] text-2xl" />
                <span className="font-extrabold text-transparent text-lg bg-clip-text bg-gradient-to-l from-[#299da8] to-[#4175EB]">Investment Timeline</span>
              </div>
              <div className="w-full" style={{ minHeight: "220px" }}>
                <Line
                  data={{
                    labels: filteredTransactions.map(t => safeStr(t.date)),
                    datasets: [
                      { label: "Invested",  data: filteredTransactions.map(t => t.type === "buy"      ? t.amount : 0), backgroundColor: "rgba(63,163,255,0.05)", borderColor: "#29B8C6", fill: true, tension: 0.36 },
                      { label: "Withdrawn", data: filteredTransactions.map(t => t.type === "withdraw" ? t.amount : 0), backgroundColor: "rgba(232,247,225,0.18)", borderColor: "#7bedaf", fill: true, tension: 0.33 },
                      { label: "Sold",      data: filteredTransactions.map(t => t.type === "sell"     ? t.amount : 0), backgroundColor: "rgba(255,182,203,0.18)", borderColor: "#ff417a", fill: true, tension: 0.28 },
                    ],
                  }}
                  options={{ responsive: true, plugins: { legend: { position: "top", labels: { color: "#668ec3", font: { size: 13, weight: "bold" }, boxWidth: 20, usePointStyle: true } } }, scales: { y: { beginAtZero: true }, x: {} }, maintainAspectRatio: false }}
                  height={220}
                />
              </div>
            </div>

            {barData.labels.length > 1 && (
              <div className="relative min-h-[360px] mt-10 bg-gradient-to-bl from-[#eaf6ff] via-[#fff0fc] to-[#daf1e9] shadow rounded-[2.5rem] px-10 py-10 flex flex-col items-center lg:col-span-5 border-2 border-[#cddbee]">
                <div className="mb-7 flex items-center gap-2">
                  <GiWallet className="text-[#57cab2]/80" size={26} />
                  <span className="font-extrabold text-transparent text-[17px] bg-clip-text bg-gradient-to-r from-[#32C6DC] via-[#488cfb] to-[#845ec2] uppercase tracking-widest">Holdings by Stock</span>
                </div>
                <div className="w-full" style={{ minHeight: "220px" }}>
                  <Bar data={barData} options={{ responsive: true, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx: any) => `₹${Number(ctx.raw).toLocaleString("en-IN")}` } } }, scales: { x: { ticks: { color: "#4678a7" } }, y: { ticks: { color: "#269972", callback: (v: any) => Number(v).toLocaleString("en-IN") }, beginAtZero: true } }, maintainAspectRatio: false }} height={230} />
                </div>
              </div>
            )}

            <div className="col-span-full mt-8">
              <div className="bg-white rounded-lg shadow flex flex-col md:flex-row items-center md:justify-between p-4 gap-5 border border-[#e4e9f7]">
                <span className="font-bold text-[#467b99] flex items-center gap-2"><TbHistory className="inline text-[#b48aea]" /> Yearly Investment Summary</span>
                <div className="overflow-x-auto">
                  <table className="text-xs min-w-[350px] text-[#657697] mx-auto">
                    <thead><tr>{Object.keys(getYearlySummary()).map(yr => <th key={yr} className="px-3 py-2">{yr}</th>)}</tr></thead>
                    <tbody><tr>{Object.values(getYearlySummary()).map((amt, i) => <td key={i} className="px-3 py-1 font-bold">{formatMoney(amt, { compact: true, symbol: true })}</td>)}</tr></tbody>
                  </table>
                </div>
              </div>
            </div>
          </section>
        </main>
      </div>

      {selectedTransaction && (
        <TransactionDetailModal transaction={selectedTransaction.transaction} onClose={() => setSelectedTransaction(null)} stock={selectedTransaction.stock} category={selectedTransaction.category} party={selectedTransaction.party} />
      )}
      {editTransactionModal && (
        <TransactionEditModal transaction={editTransactionModal.transaction} onClose={() => setEditTransactionModal(null)} onSave={handleSaveEditedTransaction} />
      )}

      <footer className="sticky left-0 bottom-0 w-full z-50 shadow bg-[#ecf5fd] px-8 py-2 flex flex-wrap items-center text-[13px] gap-7 font-semibold text-[#436095]">
        <div className="flex gap-3 items-center">
          <FaRegArrowAltCircleDown className="text-[#29b87f]" /> Withdraw
          <span className="mx-2">|</span>
          <FaRegArrowAltCircleUp className="text-[#ed0e25]" /> Sell
          <span className="mx-2">|</span>
          <IoWalletSharp className="text-[#F49A40]" /> Invest
        </div>
        <span className="flex-1 text-[#7da6c1] ml-3">Each row in holdings = one purchase lot (FIFO order). Click "📊 Detail" to see full invoice breakdown.</span>
      </footer>

      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/80">
          <div className="bg-gradient-to-tr from-[#ecfcff] via-[#e3eafe] to-[#f9fbfe] p-10 rounded-2xl shadow-lg flex flex-col items-center">
            <span className="text-2xl mb-1 text-[#2296ec] animate-bounce"><TbHistory /></span>
            <span className="font-bold text-[#357fb2] text-lg tracking-wider mb-2">Loading data...</span>
            <span className="text-[#8dbad6] text-xs">Please wait</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserHistory;

